// paywall.jsx — Vital Pro: the purchase sheet, the membership sheet, and the
// shared entitlement state both read from.
//
// ─────────────────────────────────────────────────────────────────────────
//  WHAT LIVES HERE
//    useProEntitlement()  the ONE hook every Pro-aware screen reads. Re-reads
//                         on `vital:entitlement-changed` (db.js), so a purchase
//                         unlocks the whole app at once.
//    ProSheet             ‼️ WHAT EVERY ENTRY POINT RENDERS. Picks between the
//                         two sheets below from the SERVER entitlement.
//    PaywallSheet         the purchase screen — for someone who has not bought.
//    ProStatusSheet       the membership screen — for someone who has.
//    ProActivationSuccessScreen
//                         the one-time, server-verified post-purchase page.
//    ProEntryCard         the permanent Profile entry (App Review needs it).
//    ProBadge             the "PRO" mark on the Profile identity card.
//
//  ‼️ NEVER RENDER PaywallSheet DIRECTLY. Rendering it unconditionally is what
//  showed a paying nurse the price of the thing she already pays for — she
//  reads that as "my payment failed", and it produces a support message or a
//  second purchase attempt. Render ProSheet; it cannot make that mistake.
//
// ─────────────────────────────────────────────────────────────────────────
//  WHAT PRO SELLS, AND WHAT IT MUST NEVER SELL
//
//  Pro sells DEPTH — and ONLY these five things, because these are the only
//  five `isPro` actually gates anywhere in the app:
//    1. course watches                      (screens.jsx watch editor + notify-cron
//                                            sweepCourseRule → pro_user_ids())
//    2. the CNE route                       (screens.jsx RoutePlanCard, isPro branch)
//    3. unlimited AI certificate scanning   (screens.jsx gateAiScan / runAiScan)
//    4. certificates appended to the export (screens.jsx includeEvidence && isPro)
//    5. custom reminder lead times 0–14     (screens.jsx openLeadEditor)
//
//  ‼️ THIS SAID "FOUR" UNTIL 2026-08-10, AND IT WAS WRONG — course watches were
//  gated in two places and sold in none. The settings row even read 「Vital Pro
//  功能。」 to a free user and opened THIS sheet, which then failed to mention
//  the thing she had just tried to use. When you add a gate anywhere in the app,
//  this list is the second half of that change.
//
//  ‼️ TWO OF THE FIVE RECUR: the route and the watches. The other three fire
//  once each — certificates appended every three years, a backlog scan at
//  signup, a reminder setting set and forgotten — which is why an annual
//  subscription needs the recurring two to lead. Ask anything added here the
//  same question: does it happen again next month?
//
//  ‼️ WHAT THE ROUTE MAY NOT CLAIM. The card prints 「最平 / cheapest」 ONLY when
//  cneRoutePlan returns costCertainty: 'confirmed', which needs a membership on
//  file to resolve member vs non-member pricing (84% of the catalogue publishes
//  both). Measured 2026-08-09: every live route was 'conditional'. So the
//  benefit copy says "works out the route and the cost" and never a superlative.
//  It also never quotes a points figure — 144 of 170 card-eligible nurses have
//  logged nothing, and for them the "gap" is the whole requirement rather than a
//  real shortfall (see cycleRecordCount in data.js).
//
//  ‼️ THIS LIST USED TO CLAIM A FOURTH: "multi-cycle history export". It was
//  never gated — the archived-cycle selector in the NCHK export sheet is free
//  for everyone — and it never CAN be, because gating an export is forbidden
//  three lines below. It was removed from PRO_BENEFITS (and from the App Store
//  Connect description in iap-products.md) in the same change that added this
//  note. Before adding a benefit here, point at the `isPro` check that
//  enforces it. A promise with no gate is a refund request and a Guideline
//  2.3.1/3.1.2 metadata problem, not a marketing flourish.
//
//  It does NOT sell access to the rules. The CNE information centre, the
//  regulatory-update broadcast and the renewal reminders stay free for every
//  nurse, for two reasons: putting safety-relevant regulatory information
//  behind a paywall in a professional-compliance app is indefensible, and it
//  is the kind of thing that attracts App Store review attention.
//
//  ‼️ AND IT NEVER SELLS BACK WHAT SHE ALREADY HAS. Lapsing must not lock a
//  nurse out of her own records, certificates or NCHK exports — those are her
//  compliance evidence. See the lapse rule in
//  supabase-migration-entitlements.sql. No gate in this file or anywhere else
//  may condition a READ, an EXPORT or a DELETE on `pro`.
//
//  APP STORE REQUIREMENTS baked in below (Guideline 3.1.2 / 3.1.1):
//    • Restore Purchases is always visible.
//    • Price, duration and auto-renew terms are shown before purchase.
//    • Terms + Privacy links are present.
//    • No link out to a web purchase.
//  Removing any of these is a rejection, not a style choice.
//
//  Prices are ALWAYS read from the store (RevenueCat package.product), never
//  hardcoded — a hardcoded price is wrong the moment Apple adjusts regional
//  tiers, and showing a wrong price is both a review issue and a broken promise.
// ─────────────────────────────────────────────────────────────────────────

//  ‼️ PRO_BENEFITS IS CONTRACTUAL COPY, NOT DECORATION. App Store Connect's
//  subscription Description must not promise anything this list doesn't —
//  reviewers compare them. If you edit a benefit, update the localisations in
//  appstore-assets/iap-products.md §3 in the same change.
//
//  NO FREE TRIAL / INTRO OFFER IS CONFIGURED IN APP STORE CONNECT — but the
//  Guideline 3.1.2 disclosure IS now implemented (proIntroOffer + the block above
//  the Subscribe button). It reads the trial length and the post-trial price from
//  package.product.introPrice and renders nothing when there is no offer, so
//  enabling one is an ASC decision rather than a code change.
//  ‼️ Read iap-products.md §6.1 BEFORE enabling it: entitlement is
//  `active || grace` and RevenueCat reports a trial as active, so a trial opens
//  the one-off gates too — Trial → Audit Pack → Cancel, for free.
//  ‼️ NAME THE MOMENT, NOT THE MECHANISM — and give an EXAMPLE, not a
//  description. Reported 2026-08-10: a nurse could not tell what 「補分路線」,
//  「一鍵稽核包」 or 「自訂提醒提前日數」 even were, let alone what they were
//  worth. All three named our machinery: a route, a pack, lead days. The one
//  that always read well — 「AI 證書自動整理」 — named an object and an outcome,
//  which is the pattern the other four now follow.
//
//  Every entry carries `egZh` / `egEn`: ONE concrete example, written so she can
//  picture it without learning a single new noun. The examples are what the
//  sheet actually renders; the long `how*` text belongs to the subscriber's
//  sheet, where the question is "how do I use it", not "what is it".
//
//  ‼️ AN EXAMPLE MUST LOOK LIKE AN EXAMPLE. `eg*` always opens with 例如 / e.g.
//  Her real figures appear in a separate `you` line built from her own records
//  (herFacts in PaywallSheet) and are never mixed into these strings. Dressing a
//  sample up as her data is the one mistake this page cannot survive.
//
//  ‼️ THE `nav` / `where*` / `how*` FIELDS ARE NOT PART OF THE PROMISE. They
//  describe where a feature LIVES and how it behaves once you are inside it —
//  read only by ProBenefitRows (the subscriber's sheet) and the post-purchase
//  screen, never by the paywall's sales copy. Editing them does NOT require an
//  App Store Connect description change; editing zh/en/subZh/subEn does.
//
//  ‼️ `where*` IS A FACT ABOUT THIS BUILD AND GOES STALE SILENTLY. It is the
//  answer to "I paid — where is it?", which is the single most common thing a
//  new subscriber writes in. If you move a feature, move this string in the
//  same change. `nav` is the machine-readable half of the same answer: the host
//  maps it to an actual navigation in app.jsx (goToProFeature). A `nav` the
//  host does not handle simply renders no button — it must never throw.
const PRO_BENEFITS = [
  {
    // ‼️ FIFTH GATE, AND IT WAS MISSING FROM THIS LIST UNTIL 2026-08-10.
    //
    // Course watches are Pro-gated in BOTH places that matter — the editor
    // (screens.jsx, the 新增 button opens this very sheet) and the server sweep
    // (notify-cron, sweepCourseRule → pro_user_ids()). The settings row even
    // reads 「Vital Pro 功能。」 to a free user. So the app was telling her this
    // is what Pro buys, sending her here, and then not mentioning it.
    //
    // It also passes this list's own admission test — "does it happen again next
    // month?" — every time a matching course is listed. The header note above
    // that called the route "the only one that recurs" was written before this
    // shipped; two of the five now recur.
    //
    // ‼️ AND IT ONLY BECAME SELLABLE ON 2026-08-10 (§41). Before that the sweep
    // matched on facets alone: 48% of the courses inside its window could not be
    // registered for, and all four live 免費+網上 matches were full or closed.
    // Selling a feature whose alerts she cannot act on would have been worse
    // than not selling it. Do not re-order this above the others until
    // notify-cron is actually deployed.
    id: 'watch', icon: 'bookmark',
    zh: '有啱你嘅新課程就通知你', en: 'Get told when a course that fits appears',
    egZh: '例如：設定「免費 + 網上」,一有新課程上架就通知你。',
    egEn: 'e.g. watch for free + online — you hear the day one is listed.',
    nav: 'watch',
    whereZh: '個人 · 通知 → 課程監察',
    whereEn: 'Profile · Notifications → Course watches',
    howZh: '設定你想要嘅條件（免費、網上、主題、最少學分…），最多 10 個。有新課程上架而又報得到名,就會推送通知你 —— 每個條件對同一個課程只通知一次。',
    howEn: 'Set the conditions you want (free, online, topic, minimum points…), up to 10. When a newly listed course matches AND can still be registered for, you get a push — once per course per watch.',
  },
  {
    // ‼️ THE WORD 「最平」/"cheapest" IS DELIBERATELY ABSENT. 84% of the
    // actionable catalogue publishes member and non-member prices, and without a
    // membership on file the plan is priced at the non-member tier — measured
    // 2026-08-09, every live route came back `costCertainty: 'conditional'`.
    // Say "works out what it takes"; that is true either way.
    //
    // ‼️ NO GAP NUMBER IN THIS STATIC COPY. 145 of 190 onboarded nurses have
    // logged nothing, and for them the "gap" is the whole requirement rather
    // than a shortfall. The number appears only in the `you` line below, and
    // only when her own records support it (see herFacts in PaywallSheet).
    id: 'route', icon: 'target',
    zh: '幫你計好點樣補返啲分', en: 'Works out how to close your gap',
    egZh: '例如：仲差 16 分 → 邊幾個課程補得完、總共幾錢,免費同平嘅排先。',
    egEn: 'e.g. 16 points short → which listed courses close it and what they cost, cheapest first.',
    nav: 'route',
    whereZh: '主頁 · 補分路線卡',
    whereEn: 'Home · your route card',
    howZh: '記錄咗今個週期第一個課程之後,主頁就會出現路線卡:欠幾多分、邊幾個課程補得返、總共幾錢。填咗專業會籍之後,價錢會由「由 HK$X 起」變成你實際付的價錢。',
    howEn: 'Once you have logged your first course this cycle, the card appears on Home: what is left, which listed courses close it, and the total. Add your professional memberships and the price changes from “from HK$X” to what you actually pay.',
  },
  {
    // The one name in this list that always read well, because it names an
    // object (證書) and an outcome (自動填好) rather than a mechanism. Kept as
    // the model for the other four.
    id: 'scan', icon: 'sparkle',
    zh: '影張相,證書就入咗帳', en: 'Photograph it — the record fills itself in',
    egZh: '例如：影低證書 → 課程、日期、學分自動填好,你核對後儲存。冇每年 6 次上限。',
    egEn: 'e.g. photograph a certificate → course, dates and points filled in for you to check. No 6-a-year cap.',
    nav: 'scan',
    whereZh: '底部 ＋ · AI 智能識別',
    whereEn: 'The ＋ button · AI scan',
    howZh: '影低證書、揀 PDF 或貼上課程連結,AI 會填好課程名、日期同學分,你核對後先儲存 —— AI 有機會出錯,所以最後一步永遠由你按。',
    howEn: 'Photograph a certificate, pick a PDF or paste a course link; AI fills in the course, dates and points for you to check before saving — extraction can be wrong, so the last step is always yours.',
  },
  {
    // ‼️ 「稽核」 IS GONE FROM EVERY USER-FACING STRING (2026-08-10). It is
    // accounting language; the Council's requirement — and this app's own copy
    // next to records ("以備查核時提交", data.js) — says 查核. The paywall was
    // the only place still inventing a word, which is the harder thing to
    // notice: two vocabularies inside one app, with the unfamiliar one on the
    // page that asks for money.
    //
    // ‼️ THE NAME HERE AND THE TOGGLE IN THE EXPORT SHEET MUST MATCH. That
    // exact mismatch (paywall said 稽核包, the control said 附上證書) is what
    // sent subscribers looking for a feature name that appeared nowhere. Both
    // now read 「連證書一齊交」. Rename them together or not at all.
    id: 'audit', icon: 'cert',
    zh: '要交證書嗰陣,一次過交得出', en: 'Hand in one file when they ask for your certificates',
    egZh: '例如：12 張證書 → 自動壓縮、對應附件 A 行號、併成一份可直接遞交嘅 PDF。',
    egEn: 'e.g. 12 certificates → compressed, matched to their Annex A line numbers, merged into one submission-ready PDF.',
    nav: 'audit',
    whereZh: '個人 · 匯出持續進修記錄 → 連證書一齊交',
    whereEn: 'Profile · Export CNE record → Append certificates',
    howZh: '開啟「連證書一齊交」再匯出:證書圖片會自動壓縮、按附件 A 的行號逐一對應,併入同一份 PDF。附件 A + B 本身永遠免費,PDF 證書會提你另行附上。',
    howEn: 'Turn the toggle on before exporting: certificate images are compressed, matched to their Annex A line numbers and merged into the same PDF. Annex A + B itself is always free; PDF certificates are flagged to attach separately.',
  },
  {
    // Was 「自訂提醒提前日數」 — a settings-field label lifted onto a sales
    // page. Nobody wants "lead days"; she wants not to miss the class.
    id: 'reminders', icon: 'clock',
    zh: '提早幾耐提你,你話事', en: 'You decide how early to be reminded',
    egZh: '例如：同一堂課,7 日前、3 日前同當日各提你一次,最多 4 個。',
    egEn: 'e.g. the same class flagged 7 days, 3 days and on the day — up to four.',
    nav: 'reminders',
    whereZh: '個人 · 通知 → 課堂提醒 → 提前時間',
    whereEn: 'Profile · Notifications → Class reminders → Lead times',
    howZh: '喺「提前時間」入面可以加任何 0–14 日之間嘅數字,最多 4 個。一般提醒本身免費,Pro 開放嘅係「同時設定多個」同自訂日數。',
    howEn: 'In Lead times you can add any number of days from 0 to 14, up to four of them. Ordinary reminders stay free; Pro is what allows several at once and a custom number.',
  },
];

//  The free tier, itemised. Shown opposite Pro in the same comparison control
//  so the DELTA is visible instead of implied — which is both the strongest
//  honest argument for subscribing and the clearest way to keep the promise in
//  the header above. It doubles as the App Store story: a reviewer can see at a
//  glance exactly where free ends and paid begins.
const FREE_INCLUDED = [
  { zh: '無限 CNE 紀錄與證書儲存', en: 'Unlimited CNE records and certificate storage' },
  { zh: 'NCHK 附件 A + B 匯出（包括已存檔週期）', en: 'NCHK Annex A + B export, archived cycles included' },
  { zh: '雙牌照週期追蹤與續期提醒', en: 'Dual-licence cycle tracking and renewal reminders' },
  { zh: '課程搜尋、法規資訊與更新通知', en: 'Course search, CNE information centre and regulatory alerts' },
  { zh: 'AI 證書掃描：每年 6 次,另可免費賺取', en: 'AI certificate scanning: 6 a year, plus credits you can earn free' },
];

// Legal/marketing links. Opened through the in-app browser rather than a bare
// target="_blank": inside the Capacitor WKWebView an unhandled window.open is
// a no-op, and a DEAD Terms/Privacy link on the purchase screen is a
// Guideline 3.1.2 rejection. Falls back to window.open on the web build.
//
// `manage` is APPLE'S OWN subscription screen and the only correct destination
// for "manage / cancel / change plan": we cannot cancel, refund or downgrade an
// App Store subscription ourselves, and pointing a nurse at anything else — a
// web account page, a support mailbox — would be both wrong and a Guideline
// 3.1.1 problem. It must stay a FIXED CONSTANT: never build this URL from
// profile data, a server response or anything else a third party can influence,
// or the "manage subscription" button becomes an open redirect out of the app.
//
// ‼️ `terms` MUST POINT AT THE TERMS. It is the EULA the subscription is sold
// under and the link Guideline 3.1.2 requires; a reviewer opens it. Repointing
// it at the support page (asked for once, declined) would leave the paid tier
// with no contract behind it AND fail the guideline the link exists to satisfy.
// If a nurse needs help with a charge, that is what `support` below is for —
// a separate link, not a relabelled one.
//
// ‼️ EVERY URL HERE MUST RESOLVE. A dead Terms link is a 3.1.2 rejection, and
// /terms has 404'd in production once already (site/build.mjs says so, and it
// was 404 again on 2026-08-10 — RELEASE.md §36). Check all four after any site
// deploy; the page existing in site/ is not evidence that it is being served.
const PRO_LINKS = {
  terms: 'https://www.vitalcne.com/terms',
  privacy: 'https://www.vitalcne.com/privacy',
  support: 'https://www.vitalcne.com/support',
  manage: 'https://apps.apple.com/account/subscriptions',
};

function openProLink(url) {
  // Defence in depth for the constant above: even if a future edit makes a
  // caller pass something dynamic, only https URLs ever open.
  if (!/^https:\/\//i.test(String(url || ''))) { console.warn('openProLink refused non-https url'); return; }
  try {
    if (window.Capacitor?.Plugins?.Browser?.open) window.Capacitor.Plugins.Browser.open({ url });
    else window.open(url, '_blank', 'noopener,noreferrer');
  } catch (e) { console.warn('openProLink failed', e); }
}

// ── Shared entitlement state ───────────────────────────────────────────────
//
// ONE hook, used by every screen that renders differently for a subscriber.
//
// WHY IT EXISTS: four screens each ran their own
// `DB.getEntitlement().then(setIsPro)` on mount, and nothing told them when the
// answer changed. Buying Pro in Profile therefore left the NCHK export sheet,
// the AI scan screen and the reminder stepper still rendering the free tier
// until they happened to remount. The nurse paid and the app looked identical —
// the most avoidable reason a brand-new subscriber asks for a refund.
// `DB.syncEntitlement()` now broadcasts `vital:entitlement-changed` (db.js) and
// every instance of this hook re-reads the server row on that event.
//
// ‼️ RENDERING ONLY — same contract as db.js getEntitlement(). The server
// decides what Pro unlocks; never pass this value into a server call, and never
// gate a read, an export or a delete on it (see the lapse rule in
// supabase-migration-entitlements.sql).
const PRO_ENTITLEMENT_EMPTY = { pro: false, status: null, expiresAt: null, productId: null, source: null, promoUntil: null, loading: true };

function useProEntitlement(userId, refreshKey = 0) {
  const [ent, setEnt] = React.useState(PRO_ENTITLEMENT_EMPTY);
  React.useEffect(() => {
    let cancelled = false;
    // Fail closed across sign-out/account switches. React keeps hook state
    // between dependency changes, so without this reset Account B can briefly
    // inherit Account A's Pro presentation while the new server read is in
    // flight. This is display state only, but membership dates/source are still
    // account data and must not cross that boundary even for one frame.
    setEnt(PRO_ENTITLEMENT_EMPTY);
    const refresh = async () => {
      const e = window.DB?.getEntitlement ? await window.DB.getEntitlement() : null;
      if (cancelled) return;
      setEnt({
        pro: !!e?.pro,
        status: e?.status || null,
        expiresAt: e?.expiresAt || null,
        productId: e?.productId || null,
        // WHICH SOURCE is carrying her. A code and a subscription are both
        // "Pro", but almost everything the membership sheet says about renewal,
        // cancellation and billing is true of only one of them.
        source: e?.source || null,
        promoUntil: e?.promoUntil || null,
        loading: false,
      });
    };
    refresh();
    // Re-READ rather than trusting the event's payload: the entitlements row
    // stays the single source of truth, and a webhook that landed in between
    // is picked up for free.
    window.addEventListener('vital:entitlement-changed', refresh);
    return () => { cancelled = true; window.removeEventListener('vital:entitlement-changed', refresh); };
  }, [userId, refreshKey]);
  return ent;
}

// ── Pro benefit readiness (rendering only) ──────────────────────────────────
//
// Entitlement and readiness are deliberately separate:
//   • entitlements / server-side is_pro() decide what the user MAY do;
//   • this hook only explains whether the user has completed the optional
//     setup needed for a benefit to produce a useful result.
//
// Nothing returned here is ever sent to a privileged API or trusted by a gate.
// A modified client can at most draw a dishonest tick on its own screen. Every
// destination continues to re-read the server entitlement before doing Pro
// work, and the AI edge function / notification sweeps enforce Pro server-side.
const PRO_READINESS_EMPTY = {
  loading: true,
  readyCount: 0,
  total: PRO_BENEFITS.length,
  items: {},
};

function deriveProBenefitReadiness({ profile, records = [], push = {}, courses = [] } = {}) {
  const notif = window.DB?.getNotificationPrefs
    ? window.DB.getNotificationPrefs(profile)
    : { categories: {}, delivery: 'instant' };
  const rawSessionReminders = profile?.prefs?.notifications?.sessionReminders;
  const sessionReminders = window.DB?.getSessionReminderPrefs
    ? window.DB.getSessionReminderPrefs(profile)
    : (rawSessionReminders || { enabled: true, leadDays: [1] });
  const rules = window.normaliseCourseRules
    ? window.normaliseCourseRules(profile?.prefs?.courseRules)
    : (Array.isArray(profile?.prefs?.courseRules) ? profile.prefs.courseRules : []);

  // A native device needs BOTH the current OS permission and a server token.
  // On a non-push platform (the web app), an existing token still means alerts
  // can reach another signed-in phone; with no token there is no delivery path.
  const pushReady = push.supported
    ? push.permission === 'granted' && push.hasToken
    : push.hasToken;
  const deliveryOn = notif?.delivery !== 'off';
  const watchCategoryOn = !!notif?.categories?.saved_course_deadline?.channels?.push;

  let watch;
  if (!rules.length) {
    watch = { status: 'pending', reason: 'watch_no_rules', target: 'watch', count: 0 };
  } else if (!pushReady) {
    watch = { status: 'blocked', reason: push.permission === 'denied' ? 'push_denied' : 'push_off', target: 'push', count: rules.length };
  } else if (!watchCategoryOn) {
    watch = { status: 'blocked', reason: 'watch_category_off', target: 'types', count: rules.length };
  } else if (!deliveryOn) {
    watch = { status: 'blocked', reason: 'delivery_off', target: 'delivery', count: rules.length };
  } else {
    watch = { status: 'ready', reason: 'watch_ready', target: 'watch', count: rules.length };
  }

  let reminders;
  if (!push.reminderSupported) {
    reminders = { status: 'blocked', reason: 'reminders_device_required', target: 'push' };
  } else if (!rawSessionReminders) {
    // getSessionReminderPrefs intentionally supplies a free-tier default of
    // one day. A default the user never saved is not proof that Pro reminder
    // customisation was configured, so check the RAW stored object here.
    reminders = { status: 'pending', reason: 'reminders_not_set', target: 'reminders' };
  } else if (!sessionReminders?.enabled) {
    reminders = { status: 'pending', reason: 'reminders_off', target: 'reminders' };
  } else if (!pushReady) {
    reminders = { status: 'blocked', reason: push.permission === 'denied' ? 'push_denied' : 'push_off', target: 'push' };
  } else if (!deliveryOn) {
    reminders = { status: 'blocked', reason: 'delivery_off', target: 'delivery' };
  } else {
    reminders = {
      status: 'ready', reason: 'reminders_ready', target: 'reminders',
      leadDays: Array.isArray(sessionReminders.leadDays)
        ? sessionReminders.leadDays : [sessionReminders.leadDays].filter(Number.isInteger),
    };
  }

  let route = { status: 'pending', reason: 'route_needs_profile', target: 'profile' };
  try {
    const u = window.userFromProfile?.(profile, records);
    const licences = window.cnePlanLicences?.(u) || [];
    const lic = window.cnePlanLicence?.(licences);
    if (lic?.cycleStart && lic?.renewalDate) {
      const today = window.todayIso?.();
      const progress = window.licenseProgress?.(records, lic, today);
      const gap = Number(progress?.required || 0) - Number(progress?.earned || 0);
      if (lic.exempt || !(gap > 0)) {
        route = { status: 'ready', reason: 'route_not_needed', target: null, noNavigate: true };
      } else {
        const plan = window.cneRoutePlan?.({ records, lic, courses, profile, today });
        const notStarted = profile?.prefs?.cneNotStarted?.[lic.key || 'primary'] === lic.cycleStart;
        const progressKnown = !!((plan?.cycleRecordCount || 0) > 0 || notStarted);
        const hasRoute = !!plan?.plans?.cheapest?.courses?.length;
        if (!progressKnown) {
          route = { status: 'pending', reason: 'route_needs_progress', target: 'setup' };
        } else if (!hasRoute) {
          // This is catalogue coverage, not a setup failure. Keep it visually
          // distinct from pending user work and take her to the honest Home
          // fallback rather than inventing a task she cannot complete.
          route = { status: 'blocked', reason: 'route_catalogue_gap', target: 'route' };
        } else {
          const personalised = !!(profile?.prefs?.coursePrefs?.setAt
            || (Array.isArray(profile?.memberships) && profile.memberships.length));
          route = {
            status: 'ready',
            reason: personalised ? 'route_ready' : 'route_ready_generic',
            target: 'route',
          };
        }
      }
    }
  } catch (e) {
    // Readiness is explanatory UI. If catalogue/profile derivation fails, show
    // a recoverable neutral state and leave the real entitlement untouched.
    console.warn('[Vital Pro] route readiness unavailable', e?.message || e);
    route = { status: 'blocked', reason: 'route_unavailable', target: 'route' };
  }

  const items = {
    watch,
    route,
    // These two have no persistent activation switch. Pro makes them usable
    // immediately; treating first use as mandatory setup would manufacture
    // unfinished work and, for the export toggle, encourage oversized files.
    scan: { status: 'ready', reason: 'scan_ready', target: 'scan' },
    audit: { status: 'ready', reason: 'audit_ready', target: 'audit' },
    reminders,
  };
  return {
    loading: false,
    items,
    readyCount: Object.values(items).filter((item) => item.status === 'ready').length,
    total: PRO_BENEFITS.length,
  };
}

function useProBenefitReadiness(userId, profile, records, enabled = true) {
  const [snapshot, setSnapshot] = React.useState(profile || null);
  const [push, setPush] = React.useState({
    loading: true, supported: false, reminderSupported: false, permission: 'unknown', hasToken: false,
  });

  React.useEffect(() => { setSnapshot(profile || null); }, [profile]);

  React.useEffect(() => {
    let cancelled = false;
    if (!enabled || !userId) {
      setPush({ loading: false, supported: false, reminderSupported: false, permission: 'unknown', hasToken: false });
      return () => { cancelled = true; };
    }
    setPush((current) => ({ ...current, loading: true }));
    (async () => {
      const supported = !!window.PushAPI?.isSupported?.();
      const reminderSupported = !!window.LocalAPI?.isSupported?.();
      const [fresh, permissionResult, tokenResult] = await Promise.all([
        window.DB?.getProfile ? window.DB.getProfile(userId).catch(() => null) : Promise.resolve(null),
        supported && window.PushAPI?.checkPermissions
          ? window.PushAPI.checkPermissions().catch(() => null) : Promise.resolve(null),
        window.sb
          ? window.sb.from('device_tokens').select('*', { count: 'exact', head: true }).eq('user_id', userId)
          : Promise.resolve({ count: 0, error: null }),
      ]);
      if (cancelled) return;
      if (fresh) setSnapshot(fresh);
      if (tokenResult?.error) console.warn('[Vital Pro] device token readiness check failed', tokenResult.error.message);
      setPush({
        loading: false,
        supported,
        reminderSupported,
        permission: permissionResult?.receive || 'unknown',
        // Count-only query: readiness never needs the push token secret itself.
        hasToken: Number(tokenResult?.count || 0) > 0,
      });
    })();
    return () => { cancelled = true; };
  }, [enabled, userId]);

  return React.useMemo(() => {
    if (!enabled || push.loading) return PRO_READINESS_EMPTY;
    return deriveProBenefitReadiness({
      profile: snapshot,
      records,
      push,
      courses: (typeof COURSES !== 'undefined' && Array.isArray(COURSES)) ? COURSES : [],
    });
  }, [enabled, snapshot, records, push]);
}

// Plan name from the product id the SERVER recorded (entitlements.product_id),
// never from whatever this client last selected — after a plan change the store
// is the only thing that knows what she is actually on.
function proPlanLabel(productId, isZh) {
  const id = String(productId || '').toLowerCase();
  if (/annual|year/.test(id)) return isZh ? '年費' : 'Yearly';
  if (/month/.test(id)) return isZh ? '月費' : 'Monthly';
  return isZh ? '訂閱' : 'Subscription';
}

// expires_at is a timestamptz. Slicing the UTC string is WRONG here: a period
// ending 2026-08-09T16:00:00Z is already 10 August in Hong Kong, and telling a
// nurse her subscription ends a day early is a support ticket. Convert to the
// device's local calendar date first, then hand it to the app's own formatter.
function proLocalDateIso(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

// ── Price maths ────────────────────────────────────────────────────────────
// Everything below is DERIVED FROM THE STORE's own numbers (product.price +
// product.currencyCode). Nothing is hardcoded, so a regional price tier change
// flows through automatically and we can never advertise a saving the user
// won't actually get.
function proPackageKind(p) {
  const t = String(p?.packageType || p?.identifier || '').toLowerCase();
  if (/annual|year/.test(t)) return 'annual';
  if (/month/.test(t)) return 'monthly';
  return '';
}

function proPriceNumber(p) {
  const n = Number(p?.product?.price);
  return Number.isFinite(n) && n > 0 ? n : null;
}

// Formats an arbitrary amount in the product's OWN currency. Used only for the
// "≈ per month" line under an annual plan — never to replace the store's
// priceString, which stays the authoritative figure we charge.
//
// ‼️ `currency` ALWAYS comes from product.currencyCode — the storefront of the
// signed-in App Store account. If this app is showing US$ where HK$ is
// expected, nothing here is at fault: that device is signed into a U.S.
// storefront (a sandbox tester created without a region is the usual cause).
// Hardcoding HKD to "fix" it would advertise a price we do not charge.
// See appstore-assets/iap-products.md §8.
//
// The locale follows the APP's language, not navigator.language: a nurse
// reading 繁中 on a device set to en-US should not see the one derived figure
// formatted in a different convention from everything else on the sheet.
function proFormatAmount(amount, currency, isZh) {
  if (!Number.isFinite(amount) || !currency) return '';
  try {
    return new Intl.NumberFormat(isZh ? 'zh-HK' : 'en-HK', { style: 'currency', currency }).format(amount);
  } catch (_) { return ''; }
}

// ── Introductory offer / free trial ────────────────────────────────────────
// ‼️ GUIDELINE 3.1.2 IS NOT OPTIONAL: if a subscription carries an introductory
// offer, the purchase screen must state the trial LENGTH and the price AFTER it
// BEFORE the user buys. Omitting it is a rejection. iap-products.md §6 records
// the same rule, which is why no trial has been configured until now.
//
// ‼️ EVERYTHING HERE IS READ FROM THE STORE. The period and the post-trial price
// come from `package.product.introPrice` and `product.priceString` — never a
// constant in this file. A hardcoded "7 days" is wrong the moment the offer is
// edited in App Store Connect, and then the screen is both a broken promise and
// a metadata violation. Returns null when the product has no offer, so the
// disclosure block simply does not render.
//
// ⚠️ AN INTRO OFFER OPENS EVERY GATE, INCLUDING THE ONE-OFF ONES. `pro` is
// `status === 'active' || 'grace'` (iap-verify) and RevenueCat reports a trial
// as active, so a trial hands over the audit pack — a once-per-three-years
// value — for free. Configure the offer on the ANNUAL product only, and read
// the note in iap-products.md §6 before enabling it at all.
function proIntroOffer(p, isZh) {
  const product = p?.product;
  const intro = product?.introPrice || product?.introductoryPrice;
  if (!intro) return null;
  const units = Number(intro.periodNumberOfUnits ?? intro.period_number_of_units);
  const unit = String(intro.periodUnit || intro.period_unit || '').toUpperCase();
  const introPrice = Number(intro.price);
  if (!Number.isFinite(units) || units <= 0 || !unit) return null;
  // Only a genuinely FREE intro is described as a trial; a discounted intro is a
  // different promise and must not borrow the word.
  const isFree = Number.isFinite(introPrice) && introPrice === 0;
  const NOUN = {
    DAY: { zh: '日', one: 'day', many: 'days' },
    WEEK: { zh: '星期', one: 'week', many: 'weeks' },
    MONTH: { zh: '個月', one: 'month', many: 'months' },
    YEAR: { zh: '年', one: 'year', many: 'years' },
  }[unit];
  if (!NOUN) return null;
  const length = isZh ? `${units} ${NOUN.zh}` : `${units} ${units === 1 ? NOUN.one : NOUN.many}`;
  return { isFree, length, introPriceString: intro.priceString || '' };
}

// Whole-percent saving of annual vs 12× monthly, or 0 when it can't be stated
// honestly (missing prices, different currencies, or a saving too small to be
// worth a badge).
function proAnnualSaving(packages) {
  const annual = packages.find(p => proPackageKind(p) === 'annual');
  const monthly = packages.find(p => proPackageKind(p) === 'monthly');
  const a = proPriceNumber(annual);
  const m = proPriceNumber(monthly);
  if (a == null || m == null) return 0;
  // Comparing across currencies would be meaningless arithmetic.
  if (annual?.product?.currencyCode !== monthly?.product?.currencyCode) return 0;
  const pct = Math.round((1 - a / (m * 12)) * 100);
  return pct >= 5 ? pct : 0;
}

// ── ProBenefitRows — "I paid; where is it?" ────────────────────────────────
//
// The five benefits as expandable readiness rows: a ready row explains how the
// feature actually works and names the screen it lives on, with a button that
// goes there. Shown to SUBSCRIBERS only (the membership sheet and the
// post-purchase screen) — the paywall keeps its flat list, because someone who
// has not bought cannot be navigated into a gated feature and a sales sheet
// should not make her tap twice to read what she is buying.
//
// WHY IT EXISTS: the five benefits live across different screens, two of them
// several taps deep (the audit-pack toggle is inside the NCHK export sheet).
// A list that names them but cannot take you there is how a paying subscriber
// ends up believing a feature is missing.
//
// ‼️ NAVIGATION IS THE HOST'S JOB. This file must not know about tabs, sheets
// or profile sections — it emits a stable `nav` key and app.jsx decides what
// that means (goToProFeature). No handler, or a key the host does not know →
// the row still expands and explains itself, it just shows no button. It must
// degrade, never throw.
//
// ‼️ NAVIGATING IS NOT ENTITLEMENT. Every destination re-checks `isPro` from
// the server row on arrival; this is a shortcut through the UI, not a grant.
// Never let a nav key carry state that a destination trusts.
function proReadinessLabel(item, isZh) {
  if (!item) return isZh ? '正在檢查設定…' : 'Checking setup…';
  const count = item.count || 0;
  const days = (item.leadDays || []).slice().sort((a, b) => b - a);
  const labels = {
    watch_no_rules:       isZh ? '待設定 · 建立第一個課程監察條件' : 'Set up · create your first course watch',
    watch_ready:          isZh ? `已就緒 · 已設定 ${count} 個監察條件` : `Ready · ${count} course watch${count === 1 ? '' : 'es'} set`,
    watch_category_off:   isZh ? '需要處理 · 課程通知類別已關閉' : 'Action needed · course alerts are off',
    push_denied:          isZh ? '需要處理 · iOS 已拒絕推送通知' : 'Action needed · iOS notifications are blocked',
    push_off:             isZh ? '需要處理 · 請先開啟推送通知' : 'Action needed · turn on push notifications',
    delivery_off:         isZh ? '需要處理 · 提醒發送模式已關閉' : 'Action needed · reminder delivery is off',
    route_needs_profile:  isZh ? '待設定 · 先完成牌照及週期資料' : 'Set up · complete your licence and cycle details',
    route_needs_progress: isZh ? '待設定 · 告訴 Vital 本週期的進度' : 'Set up · tell Vital your progress this cycle',
    route_catalogue_gap:  isZh ? '暫未就緒 · 現有目錄尚未能組成完整路線' : 'Not ready yet · the current catalogue cannot build a full route',
    route_unavailable:    isZh ? '暫時無法檢查路線 · 請稍後再試' : 'Route check unavailable · try again later',
    route_not_needed:     isZh ? '已就緒 · 目前沒有待補學分' : 'Ready · no points currently need covering',
    route_ready:          isZh ? '已就緒 · 已根據你的資料個人化' : 'Ready · personalised with your details',
    route_ready_generic:  isZh ? '已就緒 · 可加入會籍資料令價格更準確' : 'Ready · add memberships for more exact pricing',
    scan_ready:           isZh ? '已就緒 · 無需設定，不限年度次數' : 'Ready · no setup and no annual scan quota',
    audit_ready:          isZh ? '已就緒 · 匯出時可選用' : 'Ready · choose it when you export',
    benefit_unlocked:     isZh ? '已解鎖 · 點擊查看用法及位置' : 'Unlocked · tap for how and where',
    reminders_not_set:   isZh ? '待設定 · 選擇你想提早多久收到提醒' : 'Set up · choose how early to be reminded',
    reminders_device_required: isZh ? '需要處理 · 請在 iPhone 應用內設定提醒' : 'Action needed · set reminders in the iPhone app',
    reminders_off:       isZh ? '待設定 · 課堂提醒已關閉' : 'Set up · class reminders are off',
    reminders_ready:     isZh
      ? `已就緒 · ${days.map((d) => d === 0 ? '當日' : `${d} 日前`).join('、') || '已設定'}`
      : `Ready · ${days.map((d) => d === 0 ? 'day of' : `${d}d before`).join(', ') || 'configured'}`,
  };
  return labels[item.reason] || (item.status === 'ready'
    ? (isZh ? '已就緒' : 'Ready')
    : (isZh ? '需要處理' : 'Action needed'));
}

function ProBenefitRows({ lang, onNavigate, readiness }) {
  const isZh = lang === 'zh';
  const [openId, setOpenId] = React.useState(null);

  return (
    <div>
      {PRO_BENEFITS.map(b => {
        const on = openId === b.id;
        const state = readiness
          ? (readiness.items?.[b.id] || null)
          : { status: 'ready', reason: 'benefit_unlocked', target: b.nav };
        const actionable = !!(state && state.status !== 'ready' && onNavigate);
        const panelId = `pro-benefit-panel-${b.id}`;
        const where = isZh ? b.whereZh : b.whereEn;
        const how = isZh ? b.howZh : b.howEn;
        return (
          <div key={b.id} className="pro-benefit-row">
            <button type="button"
              className="pro-benefit-head"
              aria-expanded={actionable ? undefined : on}
              aria-controls={actionable ? undefined : panelId}
              onClick={() => {
                if (actionable) { onNavigate(b.nav, state.target); return; }
                setOpenId(on ? null : b.id);
              }}>
              <span className={'pro-check-dot ' + (
                !state ? 'is-loading' : state.status === 'ready' ? 'is-ready'
                  : state.status === 'blocked' ? 'is-warn' : 'is-pending'
              )} aria-hidden="true">
                {state?.status === 'ready'
                  ? <Icon name="check" size={12} stroke={2.6} color="currentColor" />
                  : state?.status === 'blocked'
                    ? <Icon name="alert" size={11} stroke={2.2} color="currentColor" />
                    : <span className="pro-status-ring" />}
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 13.5, fontWeight: 700, lineHeight: 1.35 }}>
                  {isZh ? b.zh : b.en}
                </span>
                <span className="muted-2" style={{ display: 'block', fontSize: 11.5, lineHeight: 1.5, marginTop: 2 }}>
                  {proReadinessLabel(state, isZh)}
                </span>
              </span>
              {/* Rotates on open. aria-hidden because aria-expanded on the
                  button already carries the state for assistive tech. */}
              <span className={'pro-benefit-caret' + (on ? ' is-open' : '')} aria-hidden="true">
                <Icon name="chev-r" size={15} stroke={2} />
              </span>
            </button>
            {on && (
              <div id={panelId} className="pro-benefit-panel">
                {how && (
                  <div style={{ fontSize: 12, lineHeight: 1.6 }}>{how}</div>
                )}
                <div className="muted-2" style={{ fontSize: 11.5, lineHeight: 1.5, marginTop: 8 }}>
                  {isZh ? b.egZh : b.egEn}
                </div>
                {where && (
                  <div className="muted-2" style={{ fontSize: 11.5, lineHeight: 1.5, marginTop: 8 }}>
                    {isZh ? '位置：' : 'Where: '}{where}
                  </div>
                )}
                {onNavigate && b.nav && state?.noNavigate !== true && (
                  <button type="button" className="btn btn-ghost"
                    style={{ marginTop: 10, padding: '6px 14px', fontSize: 12.5 }}
                    onClick={() => onNavigate(b.nav, state?.target)}>
                    {isZh ? '前往' : 'Take me there'}
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── One-time post-purchase success page ────────────────────────────────
//
// The purchase event is only a reason to OPEN this page. It is not proof of
// access: this component independently re-reads the entitlement row and does
// not render congratulations until that server read says `pro`. A forged
// browser event can therefore open an activation checker, never unlock Pro or
// manufacture a successful-purchase message.
//
// Navigation is similarly closed over PRO_BENEFITS + derived readiness. Event
// payloads, profile text and server error strings can never choose a route.
function ProActivationSuccessScreen({ lang, userId, profile, records, onLater, onNavigate }) {
  const isZh = lang === 'zh';
  const [refreshKey, setRefreshKey] = React.useState(0);
  const [retrying, setRetrying] = React.useState(false);
  const headingRef = React.useRef(null);
  const ent = useProEntitlement(userId, refreshKey);
  const readiness = useProBenefitReadiness(userId, profile, records, !!ent.pro);

  React.useEffect(() => {
    const frame = requestAnimationFrame(() => headingRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [ent.loading, ent.pro]);

  const retry = async () => {
    if (retrying) return;
    setRetrying(true);
    try { await window.DB?.syncEntitlement?.(); }
    catch (e) { console.warn('[Vital Pro] activation recheck failed', e?.message || e); }
    setRefreshKey((n) => n + 1);
    setRetrying(false);
  };

  const actionable = PRO_BENEFITS.map((benefit) => ({
    benefit,
    state: readiness.items?.[benefit.id],
  })).filter(({ state }) => state && state.status !== 'ready' && state.target && state.noNavigate !== true);
  const next = actionable[0] || null;
  const remaining = readiness.loading
    ? 0
    : PRO_BENEFITS.filter((benefit) => readiness.items?.[benefit.id]?.status !== 'ready').length;

  if (ent.loading) {
    return (
      <div className="pro-success-layer" role="dialog" aria-modal="true" aria-labelledby="pro-activation-title">
        <div className="pro-success-state" role="status" aria-live="polite">
          <span className="vital-spinner pro-success-spinner" aria-hidden="true" />
          <h1 id="pro-activation-title" ref={headingRef} tabIndex={-1}>
            {isZh ? '正在啟用 Vital Pro…' : 'Activating Vital Pro…'}
          </h1>
          <p>{isZh ? '正在向伺服器確認你的會員資格。' : 'Confirming your membership with the server.'}</p>
        </div>
      </div>
    );
  }

  if (!ent.pro) {
    return (
      <div className="pro-success-layer" role="dialog" aria-modal="true" aria-labelledby="pro-activation-title">
        <div className="pro-success-state is-error">
          <span className="pro-success-state-icon" aria-hidden="true"><Icon name="alert" size={24} stroke={2} /></span>
          <h1 id="pro-activation-title" ref={headingRef} tabIndex={-1}>
            {isZh ? '尚未完成啟用確認' : 'Activation is not confirmed yet'}
          </h1>
          <p>{isZh
            ? '你的購買可能仍在同步。重新確認前，Vital 不會提前顯示已啟用或開放付費功能。'
            : 'Your purchase may still be syncing. Vital will not claim activation or open paid features before confirming it.'}</p>
          <div className="pro-success-state-actions">
            <button type="button" className="btn btn-primary" disabled={retrying} onClick={retry}>
              {retrying ? (isZh ? '正在重新確認…' : 'Checking again…') : (isZh ? '重新確認' : 'Check again')}
            </button>
            <button type="button" className="btn btn-ghost" onClick={onLater}>
              {isZh ? '稍後處理' : 'Do this later'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const onPromo = ent.source === 'promo';
  const planLabel = onPromo ? (isZh ? '優惠碼開通' : 'Access code') : proPlanLabel(ent.productId, isZh);
  const untilIso = proLocalDateIso(onPromo ? ent.promoUntil : ent.expiresAt);
  const until = untilIso ? fmtDate(untilIso, lang) : '';
  const progress = readiness.total ? Math.round((readiness.readyCount / readiness.total) * 100) : 0;

  return (
    <div className="pro-success-layer" role="dialog" aria-modal="true" aria-labelledby="pro-activation-title">
      <div className="pro-success-page">
        <div className="pro-success-scroll">
          <header className="pro-success-hero">
            <span className="pro-success-check" aria-hidden="true">
              <Icon name="check" size={26} stroke={2.5} color="currentColor" />
            </span>
            <span className="pro-hero-mark">
              <VitalLogo size={15} color="var(--primary-ink)" />
              {isZh ? 'Vital Pro 會員' : 'Vital Pro member'}
            </span>
            <h1 id="pro-activation-title" ref={headingRef} tabIndex={-1}>
              {isZh ? '恭喜，Vital Pro 已啟用' : 'Congratulations — Vital Pro is active'}
            </h1>
            <p>{until
              ? (isZh ? `${planLabel} · 本期有效至 ${until}` : `${planLabel} · current period runs through ${until}`)
              : (isZh ? `${planLabel} · 已啟用` : `${planLabel} · active`)}</p>
          </header>

          <main className="pro-success-content">
            <section className="pro-success-summary" aria-labelledby="pro-readiness-title">
              <div className="pro-success-summary-top">
                <div>
                  <h2 id="pro-readiness-title">{isZh ? '權益設定進度' : 'Benefit setup progress'}</h2>
                  <p>{isZh ? '所有 Pro 權益已解鎖；勾號代表已按你的設定準備好。' : 'Every Pro benefit is unlocked; a tick means it is ready with your settings.'}</p>
                </div>
                <strong>{readiness.loading ? '…' : `${readiness.readyCount} / ${readiness.total}`}</strong>
              </div>
              <div className="pro-success-progress" role="progressbar"
                aria-label={isZh ? '已就緒權益' : 'Benefits ready'}
                aria-valuemin="0" aria-valuemax={readiness.total || PRO_BENEFITS.length}
                aria-valuenow={readiness.readyCount}>
                <span style={{ width: `${progress}%` }} />
              </div>
            </section>

            <section className="pro-success-benefits" aria-label={isZh ? 'Vital Pro 權益狀態' : 'Vital Pro benefit status'}>
              <ProBenefitRows lang={lang} readiness={readiness} onNavigate={onNavigate} />
            </section>

            <p className="pro-success-note">
              {isZh
                ? '設定是選擇性的，不會影響你的 Pro 會員資格。你可以稍後從「個人」內的 Vital Pro 卡片回到權益頁。'
                : 'Setup is optional and does not affect your Pro membership. You can return later from the Vital Pro card in Profile.'}
            </p>
          </main>
        </div>

        <footer className="pro-success-actions">
          <button type="button" className="btn btn-primary btn-fill"
            disabled={readiness.loading}
            onClick={() => next ? onNavigate?.(next.benefit.nav, next.state.target) : onLater?.()}>
            {readiness.loading
              ? (isZh ? '正在檢查設定…' : 'Checking setup…')
              : next
                ? (next.state.status === 'blocked'
                    ? (isZh ? `處理下一項（尚有 ${remaining} 項）` : `Fix next item (${remaining} remaining)`)
                    : (isZh ? `開始設定（尚有 ${remaining} 項）` : `Start setup (${remaining} remaining)`))
                : (isZh ? '完成' : 'Done')}
          </button>
          {next && (
            <button type="button" className="btn btn-ghost btn-fill" onClick={onLater}>
              {isZh ? '稍後設定' : 'Set up later'}
            </button>
          )}
        </footer>
      </div>
    </div>
  );
}

function PaywallSheet({ lang, onClose, onPurchased, onEarnInstead, trigger, records, profile }) {
  const isZh = lang === 'zh';
  const [packages, setPackages] = React.useState([]);
  // Her own numbers, so the sheet argues from HER situation instead of a
  // generic feature list. Both are best-effort: null/absent simply drops the
  // line rather than inventing one. Never used to gate anything — this is
  // rendering only (see the isPro contract in db.js).
  const [credits, setCredits] = React.useState(null);
  // The free list is no longer a tab — it is a disclosure below the CTA. A tab
  // made the page a comparison table whose other half was hidden, and it cost
  // the price its place on the first screen.
  const [freeOpen, setFreeOpen] = React.useState(false);
  // Selection is held as an IDENTIFIER, not the package object. Object
  // identity would silently break the moment the packages array is refetched
  // or re-created, leaving nothing selected and the CTA disabled.
  const [selectedId, setSelectedId] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState('');
  // Access code entry. Collapsed by default: it is for the minority who have
  // one, and an always-open field on a purchase screen reads as "there is a
  // discount you are missing" to everyone who does not.
  const [codeOpen, setCodeOpen] = React.useState(false);
  const [code, setCode] = React.useState('');
  const [codeBusy, setCodeBusy] = React.useState(false);
  const [codeMsg, setCodeMsg] = React.useState(null);   // { ok, text }

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      const raw = window.PurchaseAPI ? await window.PurchaseAPI.getPackages() : [];
      if (cancelled) return;
      // Annual leads. It is the pre-selected plan and the one carrying the
      // saving badge, so it must also be the one the eye lands on first —
      // reading order and recommendation should not disagree. The store
      // returns packages in its own order, so sort rather than assume.
      const rank = (p) => (proPackageKind(p) === 'annual' ? 0 : proPackageKind(p) === 'monthly' ? 1 : 2);
      const pkgs = raw.slice().sort((a, b) => rank(a) - rank(b));
      setPackages(pkgs);
      // Annual pre-selected. A Hong Kong nurse's mental cycle is the 3-year
      // practising certificate, so a monthly frame fights the way she already
      // thinks about this; annual also reads as better value against it.
      const annual = pkgs.find(p => proPackageKind(p) === 'annual');
      setSelectedId((annual || pkgs[0])?.identifier || null);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  // Balance snapshot for the "where you stand" strip. Its own effect so a
  // failure here can never stop the prices — the purchasable content — from
  // rendering.
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!window.DB?.getScanCreditStatus) return;
      const c = await window.DB.getScanCreditStatus();
      if (!cancelled) setCredits(c);
    })();
    return () => { cancelled = true; };
  }, []);

  // Records still missing a certificate — the concrete size of the audit-pack
  // problem for THIS nurse. `records` is optional: call sites that don't have
  // it simply don't show the line.
  const missingCerts = Array.isArray(records)
    ? records.filter(r => r && !r.certificate && !r.certificateUrl && !r.certificate_url).length
    : 0;

  const selected = packages.find(p => p.identifier === selectedId) || null;
  const savingPct = proAnnualSaving(packages);

  const priceOf = (p) => p?.product?.priceString || p?.product?.price_string || '';
  const periodOf = (p) => {
    const kind = proPackageKind(p);
    if (kind === 'annual') return isZh ? '每年' : 'per year';
    if (kind === 'monthly') return isZh ? '每月' : 'per month';
    return '';
  };
  // Noun form ("Yearly" / "年費") for the plan card's own heading, where
  // periodOf's prepositional form ("per year") would read as a fragment.
  const periodLabelOf = (p) => {
    const kind = proPackageKind(p);
    if (kind === 'annual') return isZh ? '年費' : 'Yearly';
    if (kind === 'monthly') return isZh ? '月費' : 'Monthly';
    return isZh ? '訂閱' : 'Plan';
  };
  // "≈ HK$10.67 / month" under the annual plan. Shown as an aid to comparison
  // only — clearly approximate, and never in place of the real charge.
  const perMonthOf = (p) => {
    if (proPackageKind(p) !== 'annual') return '';
    const price = proPriceNumber(p);
    const formatted = proFormatAmount(price != null ? price / 12 : NaN, p?.product?.currencyCode, isZh);
    if (!formatted) return '';
    return isZh ? `≈ ${formatted} / 月` : `≈ ${formatted} / month`;
  };

  const buy = async () => {
    if (!selected || busy) return;
    setBusy(true); setErr('');
    const res = await window.PurchaseAPI.purchase(selected);
    if (res.cancelled) { setBusy(false); return; } // user backed out — say nothing
    if (!res.ok) {
      setErr(lang === 'zh' ? '購買未完成,請稍後再試。' : 'Purchase did not complete. Please try again.');
      setBusy(false); return;
    }
    // The purchase succeeded on Apple's side. Access still comes from the
    // SERVER, so ask it to re-read the store before we claim anything.
    let ent = await window.DB.syncEntitlement();
    // ONE short retry. RevenueCat has occasionally not finished recording the
    // transaction by the time we ask, milliseconds after StoreKit returns —
    // and the failure mode is the worst one available: a nurse who has just
    // been charged is told to go and tap "Restore purchases". A single re-ask
    // converts most of those into an ordinary success. Bounded at one attempt
    // on purpose; if it is still not there, the honest message below is right.
    if (!ent?.pro) {
      await new Promise(r => setTimeout(r, 1500));
      ent = await window.DB.syncEntitlement();
    }
    setBusy(false);
    if (ent.pro) {
      onPurchased?.(ent);
      onClose?.();
      // ‼️ DISPATCH LAST, AND ONLY FROM THE PURCHASE PATH.
      //
      // LAST because `onClose` first dismisses Apple's completed purchase
      // flow. The app-level listener then navigates to the separate activation
      // page, which independently verifies the entitlement before showing its
      // success state; it never silently sends her into setup.
      //
      // AN EVENT rather than more props because five call sites open this sheet
      // (Profile, AI quota, the export sheet, the renewal card, the route card)
      // and only two of them had wired a post-purchase handler. A broadcast
      // cannot be forgotten by the sixth.
      //
      // ONLY THE PURCHASE PATH: `restore()` deliberately does not fire this. A
      // returning subscriber reinstalling on a new phone has not just bought
      // anything, and congratulating her on a purchase she made months ago
      // reads as a duplicate charge.
      //
      // ‼️ THE EVENT GRANTS NOTHING. It carries the server-confirmed entitlement
      // for convenience only; every listener re-reads the row (useProEntitlement)
      // and every destination re-checks isPro on arrival.
      try {
        window.dispatchEvent(new CustomEvent('vital:pro-purchased', { detail: ent }));
      } catch (_) { /* non-browser host — nothing to notify */ }
    } else {
      // Point at a button that ACTUALLY exists — the one directly below. There
      // is no "Restore purchases" row in Profile yet; see the Stage 4 note in
      // RELEASE.md §27 before turning subscriptions on.
      setErr(lang === 'zh'
        ? '購買已完成,但啟用需要一點時間。請稍等片刻,然後按下面的「回復購買」。'
        : 'Purchase went through, but activation is taking a moment. Wait a few seconds, then tap “Restore purchases” below.');
    }
  };

  // ‼️ EVERY BRANCH IS NAMED BY THE SERVER, and the client only translates.
  // Inventing a friendlier message for a reason we did not get back is how a
  // rate-limited user ends up being told her code is invalid.
  const CODE_MSG = {
    ok:               null,   // handled separately — it carries the day count
    invalid:          isZh ? '請輸入優惠碼。' : 'Enter a code.',
    not_found:        isZh ? '呢個優惠碼用唔到 —— 可能輸入錯咗、已經過期或者派完。'
                           : 'That code cannot be used — it may be mistyped, expired or fully redeemed.',
    already_redeemed: isZh ? '你已經用咗呢個優惠碼。' : 'You have already used this code.',
    rate_limited:     isZh ? '嘗試次數太多,請一小時後再試。' : 'Too many attempts. Try again in an hour.',
    unauthenticated:  isZh ? '請先登入。' : 'Please sign in first.',
    network:          isZh ? '連線有問題,請再試一次。' : 'Something went wrong. Please try again.',
  };

  const submitCode = async () => {
    const raw = code.trim();
    if (!raw || codeBusy || !window.DB?.redeemPromoCode) return;
    setCodeBusy(true); setCodeMsg(null);
    const res = await window.DB.redeemPromoCode(raw);
    setCodeBusy(false);
    if (res.ok) {
      const until = proLocalDateIso(res.proUntil);
      setCode('');
      setCodeMsg({ ok: true, text: isZh
        ? `已開通 Vital Pro ${res.grantedDays} 日${until ? `,有效至 ${fmtDate(until, lang)}` : ''}。`
        : `Vital Pro is on for ${res.grantedDays} days${until ? `, through ${fmtDate(until, lang)}` : ''}.` });
      // The entitlement broadcast already fired inside DB.redeemPromoCode, so
      // every Pro-aware screen has updated. Close on a short delay so she reads
      // the confirmation instead of watching the sheet vanish.
      setTimeout(() => { onPurchased?.({ pro: true }); onClose?.(); }, 1400);
      return;
    }
    setCodeMsg({ ok: false, text: CODE_MSG[res.reason] || CODE_MSG.network });
  };

  const restore = async () => {
    if (busy) return;
    setBusy(true); setErr('');
    await window.PurchaseAPI.restore();
    const ent = await window.DB.syncEntitlement();
    setBusy(false);
    if (ent.pro) { onPurchased?.(ent); onClose?.(); }
    else setErr(lang === 'zh' ? '找不到可回復的訂閱。' : 'No previous subscription found to restore.');
  };

  // ── HER figures, from her own records ─────────────────────────────────────
  //
  // ‼️ THIS IS THE ONLY THING ON THE PAGE THAT ANOTHER APP COULD NOT WRITE. A
  // feature list is a claim; "你尚欠 16 分,目錄裡最平 HK$1,000 補得完" is a
  // fact about her, and it is the strongest honest argument this sheet has.
  //
  // ‼️ IT NEVER SHOWS THE ROUTE ITSELF. The gap and the total are proof that the
  // planner works; the COURSE NAMES are the thing she is buying. Printing them
  // here would hand over the deliverable at the moment of asking for money.
  //
  // ‼️ AND IT STAYS SILENT WHEN SHE HAS LOGGED NOTHING. With no records the
  // "gap" is the whole requirement — an arithmetic fact about an empty table,
  // not a finding about her (see cycleRecordCount in data.js). 145 of 190
  // onboarded nurses are in exactly that state, so this must fail closed:
  // no records → no number → the generic examples carry the page.
  const herFacts = React.useMemo(() => {
    try {
      const u = window.userFromProfile?.(profile, records);
      const lic = window.cnePlanLicence?.(window.cnePlanLicences?.(u) || []);
      if (!lic || !window.cneRoutePlan) return null;
      const plan = window.cneRoutePlan({
        records, lic, courses: window.COURSES, profile, today: window.todayIso?.(),
      });
      if (!plan || !plan.cycleRecordCount) return null;
      const best = plan.plans?.cheapest;
      return {
        gap: plan.gap,
        cost: best && best.courses?.length ? best.cost : null,
        costCertain: plan.costCertainty === 'confirmed',
        deadline: plan.window?.end || null,
      };
    } catch (_) { return null; }   // a sales page must never throw
  }, [profile, records]);

  // The `you` line for a benefit, or '' when we cannot say anything true.
  const herLine = (id) => {
    if (id === 'route' && herFacts && herFacts.gap > 0) {
      const gap = Number.isInteger(herFacts.gap) ? herFacts.gap : herFacts.gap.toFixed(1);
      if (herFacts.cost == null) {
        return isZh ? `你而家：尚欠 ${gap} 分` : `You: ${gap} points short`;
      }
      const money = `HK$${Math.round(herFacts.cost).toLocaleString('en-US')}`;
      // "由…起" whenever a member/non-member price could not be resolved — the
      // same hedge the route card itself prints. Never claim 最平 here.
      const from = herFacts.costCertain ? money : (isZh ? `由 ${money} 起` : `from ${money}`);
      return isZh
        ? `你而家：尚欠 ${gap} 分,目錄裡 ${from} 補得完`
        : `You: ${gap} points short — ${from} of listed courses closes it`;
    }
    // ‼️ THRESHOLD, NOT A COUNT. "1 筆未附證書" is not a pile, and printing it
    // beside a subscribe button argues AGAINST buying. Below three, the generic
    // example is the more honest line.
    if (id === 'audit' && missingCerts >= 3) {
      return isZh
        ? `你而家：${missingCerts} 筆紀錄未附上證書`
        : `You: ${missingCerts} records without a certificate`;
    }
    if (id === 'scan' && credits && credits.available === 0) {
      return isZh ? '你而家：今年的免費 AI 掃描已用完' : 'You: this year’s free AI scans are used up';
    }
    return '';
  };

  const unavailable = !loading && packages.length === 0;
  // ‼️ TWO DIFFERENT UNAVAILABILITIES, AND TELLING THEM APART IS THE DIFFERENCE
  // BETWEEN AN HONEST WAIT AND A LIE.
  //   • native, store not answering → "請稍後再試" is TRUE; it works once the
  //     store does (RevenueCat not yet installed — see PurchaseAPI, RELEASE §25).
  //   • web                        → there is no in-app purchase on this
  //     platform, ever. "Try again later" describes a wait that never ends.
  // Home no longer routes anyone here from the web (the route card's onExplore
  // is gated on window.proPurchasable), but Profile's Vital Pro row still does,
  // and a nurse who bought on her iPhone can legitimately arrive here on the web
  // wondering where her subscription is — so the copy has to name the platform
  // and tell her that a purchase made in the app is already active here.
  const noStoreHere = !(globalThis.Capacitor?.isNativePlatform?.());

  // Why the sheet was opened. Kept as one lookup so every entry point is
  // visible at a glance and none can silently fall through to no context —
  // `renewal` previously did exactly that.
  const CONTEXT = {
    quota: isZh
      ? '你已用完今年的免費 AI 掃描次數。升級後不受每年 6 次上限，或繼續用手動輸入（永遠免費）。'
      : 'You’ve used this year’s free AI scans. Pro removes the annual six-scan quota — or keep logging manually, which is always free.',
    audit: isZh
      ? '附件 A + B 一直可免費匯出。Vital Pro 可以連證書一齊交:自動壓縮、對應行號,併成一份文件。'
      : 'Annex A + B always exports free. Vital Pro can compress image certificates, match their line numbers and merge them into one audit pack.',
    renewal: isZh
      ? '續期在即。你的紀錄與附件 A + B 匯出永遠免費;Vital Pro 只是把證書一次過整理成可直接遞交的一份文件。'
      : 'Renewal is close. Your records and the Annex A + B export are always free — Vital Pro simply turns your certificates into one submission-ready audit pack.',
    // Reached ONLY from Home's route card, which renders only when a real route
    // exists — so this copy never describes something the catalogue cannot do
    // right now. ‼️ The route is deliberately NOT in PRO_BENEFITS yet: that list
    // is the contractual promise shown to everyone, and until the catalogue
    // carries cne_relation classifications a subscriber could buy on the promise
    // and find no route card at all. Add it there in the same change that lands
    // the classification data, and update iap-products.md §3 with it.
    route: isZh
      ? '你尚欠的分數,目錄裡有課程可以補。Vital Pro 幫你計出用哪些課程、花多少錢最划算 —— 你的缺口數字本身永遠免費。'
      : 'The points you still owe can be covered by courses already listed. Vital Pro works out which ones and what they cost — your shortfall figure itself is always free.',
  };
  const context = CONTEXT[trigger] || '';

  // The old "where you stand" chip strip lived here. It is gone: every fact it
  // carried now sits INSIDE the benefit it argues for (herLine above), which is
  // where she is already reading and where the fact means something. Two rules
  // it established are kept, and both moved into herLine:
  //   • A REMAINING free-scan quota argues against buying, so it is never shown
  //     on this sheet — only a spent one is.
  //   • A fact is only worth printing at the size of the thing it argues for:
  //     "1 筆未附證書" is not a pile, hence the threshold of three.

  return (
    <Sheet open={true} onClose={onClose} title="Vital Pro">
      {/* Hero is full-bleed, so it sits OUTSIDE the 20px body gutter. The
          Sheet's own title row (with the close button) stays above it —
          consistent with every other sheet in the app, and it keeps the
          dialog's accessible name. */}
      <div className="pro-hero">
        <span className="pro-hero-mark">
          <VitalLogo size={15} color="var(--primary-ink)" />
          Vital Pro
        </span>
        {/* ‼️ THIS HEADLINE MAY ONLY PROMISE WHAT `isPro` ALREADY GATES —
            the same rule PRO_BENEFITS lives under, for the same reason. The
            previous version ("續期同查核,一次過準備好") framed Pro around an
            event that happens once per three-year cycle, which is why the
            subscription reads as something to buy for one month and cancel;
            and its sub-line ("只接手最花時間的整理工作") sold Pro as a chore
            remover, which is worth a one-off fee, not an annual one.
            This wording re-frames the SAME three gates without claiming
            anything new. Do not reach for planning/monitoring language until a
            planning/monitoring gate actually ships. */}
        {/* THE END STATE, not the machinery. Four unfamiliar feature names
            compete with each other and none of them survives; one sentence
            about the moment she actually worries about does. */}
        <h2>{isZh ? '續期同查核嗰陣,你交得出嘢。' : 'When they ask, you have it ready.'}</h2>
        <p>
          {isZh
            ? '你的紀錄、匯出同法規資訊永遠免費。Vital Pro 幫你留意新課程、計好點樣補分、自動整理證書。'
            : 'Your records, the exports and the regulations are always free. Vital Pro watches for the courses you want, works out how to close your gap, and files your certificates for you.'}
        </p>
      </div>

      {/* 20px gutter matches the sheet's own title padding — the previous 4px
          let the plan cards hang wider than the heading above them. */}
      <div style={{ padding: '16px 20px 8px' }}>
        {/* One line on what Pro is FOR. The sheet previously opened straight
            into a feature list, which never answered "why would I buy this". */}
        {context && (
          <div style={{
            fontSize: 12.5, lineHeight: 1.55, marginBottom: 12, padding: '10px 12px',
            background: 'var(--primary-soft-2)', borderRadius: 12,
            border: '0.5px solid var(--border)',
          }}>
            {context}
          </div>
        )}

        {/* ── What she gets, as EXAMPLES ─────────────────────────────────
            Five one-line promises, each with a concrete example underneath, and
            HER figure in place of the example wherever her own records support
            one. This replaced a 免費版/Vital Pro tab control: it made the page a
            comparison table whose other half was hidden, and it pushed the price
            below the fold. The free list is still here — as a disclosure under
            the CTA, where it answers "what do I lose if I stop" rather than
            competing with the pitch. */}
        <div style={{ marginBottom: 16 }}>
          {PRO_BENEFITS.map(b => {
            const mine = herLine(b.id);
            return (
              <div key={b.id} className="pro-check">
                <span className="pro-check-dot" aria-hidden="true">
                  <Icon name="check" size={12} stroke={2.6} color="currentColor" />
                </span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 700, lineHeight: 1.35 }}>
                    {isZh ? b.zh : b.en}
                  </div>
                  {/* HER fact outranks the example — but only ever one of them,
                      so a sample can never be mistaken for her data. */}
                  {mine ? (
                    <div style={{ fontSize: 11.5, lineHeight: 1.5, marginTop: 2, color: 'var(--primary)', fontWeight: 600 }}>
                      {mine}
                    </div>
                  ) : (
                    <div className="muted-2" style={{ fontSize: 11.5, lineHeight: 1.5, marginTop: 2 }}>
                      {isZh ? b.egZh : b.egEn}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {loading && (
          <div aria-live="polite" aria-busy="true"
            style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 4 }}>
            <span className="sr-only">{isZh ? '載入價格…' : 'Loading prices…'}</span>
            <Skeleton h={58} r={14} />
            <Skeleton h={58} r={14} />
          </div>
        )}

        {unavailable && (
          <div className="muted-2" style={{ fontSize: 12.5, lineHeight: 1.5 }}>
            {noStoreHere
              ? (isZh
                  ? '訂閱只可以喺 iPhone 版 Vital App 內購買。如果你已經喺 App 內訂閱咗,喺呢度會自動生效 —— 撳下面嘅「回復購買」可以即刻重新檢查。'
                  : 'Subscriptions can only be purchased in the Vital iPhone app. If you already subscribed there, Pro is active here too — tap “Restore Purchases” below to re-check now.')
              : (isZh
                  ? '訂閱功能暫時無法使用。請稍後再試。'
                  : 'Subscriptions aren’t available right now. Please try again later.')}
          </div>
        )}

        {/* Plan picker. A real <input type="radio"> sits visually hidden inside
            each label, so arrow-key navigation, the single tab stop and the
            "1 of 2" VoiceOver announcement all come from the platform instead
            of being approximated with aria-pressed on a row of buttons. */}
        {packages.length > 0 && (
          <div role="radiogroup"
            aria-label={isZh ? '選擇訂閱期間' : 'Choose a billing period'}
            className="pro-plans">
            {packages.map(p => {
              const on = p.identifier === selectedId;
              const perMonth = perMonthOf(p);
              const showSaving = savingPct > 0 && proPackageKind(p) === 'annual';
              return (
                <label key={p.identifier} className={'pro-plan' + (on ? ' on' : '')}
                  style={{ opacity: busy && !on ? 0.6 : 1, cursor: busy ? 'default' : 'pointer' }}>
                  <input type="radio" name="vital-pro-plan" className="sr-only"
                    value={p.identifier}
                    checked={on}
                    disabled={busy}
                    onChange={() => setSelectedId(p.identifier)} />
                  {showSaving && (
                    <span className="pro-plan-badge">
                      {isZh ? `慳 ${savingPct}%` : `SAVE ${savingPct}%`}
                    </span>
                  )}
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6,
                  }}>
                    <span style={{ fontSize: 12.5, fontWeight: 700 }}>
                      {periodLabelOf(p)}
                    </span>
                    {/* Unselected state is a drawn ring rather than an icon —
                        the icon set has no hollow circle, and a ring is the
                        affordance people already read as "radio". */}
                    {on
                      ? <Icon name="check-circ" size={17} stroke={1.8} color="var(--primary)" />
                      : <span aria-hidden="true" style={{
                          width: 17, height: 17, borderRadius: 999,
                          border: '1.5px solid var(--fg-3)', flexShrink: 0, display: 'block',
                        }} />}
                  </div>
                  {/* Price string comes from the store, localised by Apple. */}
                  <div style={{ fontSize: 17, fontWeight: 700, letterSpacing: '-0.02em', marginTop: 3 }}>
                    {priceOf(p)}
                  </div>
                  <div className="muted-2" style={{ fontSize: 11, marginTop: 1 }}>
                    {perMonth || periodOf(p)}
                  </div>
                </label>
              );
            })}
          </div>
        )}

        {/* role="alert" so a purchase failure is announced, not just coloured. */}
        {err && (
          <div role="alert" style={{
            display: 'flex', gap: 8, alignItems: 'flex-start',
            fontSize: 12, lineHeight: 1.5, color: 'var(--danger)',
            background: 'color-mix(in oklch, var(--danger) 8%, transparent)',
            border: '0.5px solid color-mix(in oklch, var(--danger) 30%, transparent)',
            borderRadius: 10, padding: '9px 11px', marginTop: 10,
          }}>
            <span aria-hidden="true" style={{ flexShrink: 0, fontWeight: 700 }}>!</span>
            <span>{err}</span>
          </div>
        )}

        {/* ‼️ GUIDELINE 3.1.2 — the trial length and the price AFTER it, stated
            BEFORE the purchase button, in the store's own numbers. Renders only
            when the selected product actually carries an introductory offer, so
            it cannot describe an offer that is not configured. Never move this
            below the button, and never hardcode the period. */}
        {!unavailable && selected && (() => {
          const intro = proIntroOffer(selected, isZh);
          if (!intro) return null;
          const after = `${priceOf(selected)} ${periodOf(selected)}`;
          return (
            <div style={{
              fontSize: 11.5, lineHeight: 1.55, marginTop: 12, padding: '10px 12px',
              background: 'var(--primary-soft-2)', borderRadius: 12,
              border: '0.5px solid var(--border)',
            }}>
              {intro.isFree
                ? (isZh
                    ? `首 ${intro.length}免費試用,其後按 ${after} 自動續訂。可隨時在試用期結束前取消。`
                    : `Free for the first ${intro.length}, then ${after}, renewing automatically. Cancel any time before the trial ends.`)
                : (isZh
                    ? `首 ${intro.length}以 ${intro.introPriceString} 計算,其後按 ${after} 自動續訂。`
                    : `${intro.introPriceString} for the first ${intro.length}, then ${after}, renewing automatically.`)}
            </div>
          );
        })()}

        {!unavailable && (
          <button className="btn btn-primary" style={{ width: '100%', marginTop: 12 }}
            disabled={!selected || busy} aria-busy={busy || undefined} onClick={buy}>
            {busy
              ? (isZh ? '處理中…' : 'Working…')
              /* Name the exact charge on the button itself: the nurse should
                 never have to look back up the sheet to check what tapping
                 this costs. */
              : selected
                ? (isZh ? `訂閱 · ${priceOf(selected)} ${periodOf(selected)}`
                        : `Subscribe · ${priceOf(selected)} ${periodOf(selected)}`)
                : (isZh ? '訂閱' : 'Subscribe')}
          </button>
        )}

        {/* ── Risk reversal, directly under the button ────────────────────
            "What if I regret it" is the last thing standing between reading and
            paying, and both halves of the answer used to be far below: the
            cancellation line was here, but the promise that her records survive
            a lapse was buried inside the Free tab. They belong together, and
            they belong where the hesitation happens. */}
        {!unavailable && (
          <div className="muted-2" style={{ fontSize: 11, lineHeight: 1.55, textAlign: 'center', marginTop: 8 }}>
            {isZh
              ? '可隨時在 App Store 帳戶設定中取消。取消後你仍可完整讀取、匯出及刪除自己的所有紀錄與證書。'
              : 'Cancel anytime in your App Store account settings. If you do, you keep full access to read, export and delete every record and certificate you already have.'}
          </div>
        )}

        {/* ── What free already includes ──────────────────────────────────
            Kept, and kept honest — a nurse must never mistake her own records
            or the regulations for something she is being sold, and a reviewer
            must be able to see exactly where free ends. It is a disclosure
            rather than a tab because it answers a question that arrives AFTER
            the pitch ("so what am I already getting?"), and because as a tab it
            cost the price its place on the first screen. */}
        <div style={{ marginTop: 14 }}>
          <button type="button"
            aria-expanded={freeOpen} aria-controls="pro-free-list"
            onClick={() => setFreeOpen(v => !v)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, width: '100%',
              minHeight: 40, padding: '6px 0', background: 'none', border: 'none',
              font: 'inherit', fontSize: 12.5, color: 'var(--fg-2)', cursor: 'pointer',
            }}>
            <span className={'pro-benefit-caret' + (freeOpen ? ' is-open' : '')} aria-hidden="true">
              <Icon name="chev-r" size={14} stroke={2} />
            </span>
            {isZh ? '免費版已經包咩？' : 'What does Free already include?'}
          </button>
          {freeOpen && (
            <div id="pro-free-list" style={{ paddingLeft: 2 }}>
              {FREE_INCLUDED.map(f => (
                <div key={f.en} className="pro-check">
                  <span className="pro-check-dot" aria-hidden="true">
                    <Icon name="check" size={12} stroke={2.6} color="currentColor" />
                  </span>
                  <div style={{ fontSize: 13, lineHeight: 1.45, minWidth: 0 }}>
                    {isZh ? f.zh : f.en}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* The FREE alternative, given equal visibility. Contributing a course
            still earns a scan credit; burying that to push the subscription
            would be a dark pattern in an app nurses rely on. */}
        {onEarnInstead && (
          <button className="btn btn-ghost" style={{ width: '100%', marginTop: 8 }}
            disabled={busy} onClick={onEarnInstead}>
            {isZh ? '或免費賺取掃描次數' : 'Or earn a scan credit for free'}
          </button>
        )}

        {/* Restore Purchases is REQUIRED by App Store Guideline 3.1.1 — never
            remove it. It sits in this row rather than as a third full-width
            button because three stacked buttons buried the CTA, but the row is
            styled with a 44px target so it stays a real control, not fine
            print. Terms + Privacy are REQUIRED by 3.1.2 and ride alongside. */}
        {/* ── Access code ─────────────────────────────────────────────────
            A disclosure, not a field: most nurses do not have a code, and an
            open box on a purchase screen makes everyone feel they are paying
            too much. It sits below the free-tier list because it answers the
            narrowest question on the page. */}
        <div style={{ marginTop: 6 }}>
          <button type="button"
            aria-expanded={codeOpen} aria-controls="pro-code-form"
            onClick={() => setCodeOpen(v => !v)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, width: '100%',
              minHeight: 40, padding: '6px 0', background: 'none', border: 'none',
              font: 'inherit', fontSize: 12.5, color: 'var(--fg-2)', cursor: 'pointer',
            }}>
            <span className={'pro-benefit-caret' + (codeOpen ? ' is-open' : '')} aria-hidden="true">
              <Icon name="chev-r" size={14} stroke={2} />
            </span>
            {isZh ? '有優惠碼？' : 'Have a code?'}
          </button>
          {codeOpen && (
            <div id="pro-code-form">
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <input
                  className="auth-input"
                  style={{ flex: 1, minWidth: 0, textTransform: 'uppercase' }}
                  value={code}
                  autoCapitalize="characters" autoCorrect="off" spellCheck={false}
                  placeholder={isZh ? '輸入優惠碼' : 'Enter code'}
                  aria-label={isZh ? '優惠碼' : 'Access code'}
                  onChange={(e) => { setCode(e.target.value); setCodeMsg(null); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submitCode(); } }} />
                <button type="button" className="btn btn-ghost"
                  style={{ padding: '10px 16px', fontSize: 13, flexShrink: 0 }}
                  disabled={!code.trim() || codeBusy}
                  onClick={submitCode}>
                  {codeBusy ? (isZh ? '…' : '…') : (isZh ? '使用' : 'Apply')}
                </button>
              </div>
              {codeMsg && (
                <div role={codeMsg.ok ? 'status' : 'alert'}
                  style={{
                    fontSize: 11.5, lineHeight: 1.5, marginTop: 6,
                    color: codeMsg.ok ? 'var(--primary)' : 'var(--danger, inherit)',
                    fontWeight: codeMsg.ok ? 600 : 400,
                  }}>
                  {codeMsg.text}
                </div>
              )}
              {/* ‼️ TWO KINDS OF CODE, AND THEY ARE NOT INTERCHANGEABLE. The box
                  above takes a VITAL code, which grants free access and never
                  touches Apple. An App Store offer code (a discount on a paid
                  subscription) can only be redeemed by Apple's own sheet — we
                  cannot validate or apply one. Offering the right destination
                  for each is the difference between "code doesn't work" and a
                  purchase that completes. */}
              {window.PurchaseAPI?.isSupported?.() && (
                <button type="button"
                  onClick={() => window.PurchaseAPI.presentOfferCodeSheet()}
                  style={{
                    marginTop: 10, padding: '4px 0', minHeight: 32, background: 'none',
                    border: 'none', font: 'inherit', fontSize: 11.5, color: 'var(--primary)',
                    textDecoration: 'underline', textUnderlineOffset: 3, cursor: 'pointer',
                    textAlign: 'left',
                  }}>
                  {isZh ? 'App Store 優惠碼喺呢度使用 →' : 'Redeem an App Store offer code →'}
                </button>
              )}
            </div>
          )}
        </div>

        <div className="pro-footer-links" style={{ marginTop: 10 }}>
          <button type="button" disabled={busy} onClick={restore}>
            {isZh ? '回復購買' : 'Restore Purchases'}
          </button>
          <span className="pro-footer-sep" aria-hidden="true">·</span>
          <button type="button" onClick={() => openProLink(PRO_LINKS.support)}>
            {isZh ? '支援' : 'Support'}
          </button>
          <span className="pro-footer-sep" aria-hidden="true">·</span>
          <button type="button" onClick={() => openProLink(PRO_LINKS.terms)}>
            {isZh ? '使用條款' : 'Terms'}
          </button>
          <span className="pro-footer-sep" aria-hidden="true">·</span>
          <button type="button" onClick={() => openProLink(PRO_LINKS.privacy)}>
            {isZh ? '私隱政策' : 'Privacy'}
          </button>
        </div>

        {/* REQUIRED by 3.1.2 — auto-renew terms, shown before purchase. */}
        <div className="muted-2" style={{ fontSize: 10.5, lineHeight: 1.5, marginTop: 4, textAlign: 'center' }}>
          {isZh
            ? '訂閱會自動續期,除非在當期結束前至少 24 小時於 App Store 帳戶設定中關閉。付款於確認購買時由你的 Apple 帳戶扣除。'
            : 'Subscriptions renew automatically unless turned off at least 24 hours before the end of the current period, in your App Store account settings. Payment is charged to your Apple account at confirmation of purchase.'}
        </div>
      </div>
    </Sheet>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  ProStatusSheet — what a SUBSCRIBER sees.
//
//  ‼️ THIS EXISTS BECAUSE SHOWING A PAYING NURSE THE PURCHASE SCREEN IS A BUG.
//  Until this sheet, the Profile row opened PaywallSheet unconditionally: a
//  subscriber tapped "Vital Pro" and was asked to subscribe to Vital Pro. That
//  reads as "my payment didn't work", which produces exactly two outcomes —
//  a support message, or a double purchase attempt.
//
//  What a subscription-management screen owes the person paying for it:
//    • WHAT they are on (plan) and UNTIL WHEN (period end)
//    • an honest statement about renewal — we know the period end, and Apple
//      renews unless cancelled, so the copy says exactly that and no more
//    • a way to CANCEL or change, which on iOS means Apple's own screen
//    • the billing-problem state surfaced LOUDLY, because 'grace' means Apple
//      is retrying her card and she is days away from silently losing Pro
//    • Restore Purchases, still reachable (Guideline 3.1.1)
//
//  It sells nothing. There is no upsell, no plan switcher and no price on this
//  sheet — she has already bought; the job here is confidence and control.
// ─────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────
// ProSetupSheet — the three questions that make Pro's answers hers.
//
// WHY IT EXISTS, in one measurement: on 2026-08-09 every single live route came
// back `costCertainty: 'conditional'` — 133 of 133 — for one reason. 84% of the
// actionable catalogue publishes member AND non-member prices, and no profile
// had a membership on file, so the planner had to price every course at the
// non-member tier and label the total "from HK$X". One text field turns the
// flagship Pro feature from a hedge into a number.
//
// ‼️ ORDER IS BY IMMEDIATE PAYOFF, NOT BY DATA COMPLETENESS. Memberships lead
// because the effect is visible on the very next screen. A long form asked at
// the moment of payment is a churn risk, and every question after the first is
// borrowed goodwill — so all three are skippable and nothing breaks when they
// are skipped (costs simply stay labelled "from", ranking stays generic).
//
// ‼️ EVERY FIELD HERE HAS A CONSUMER. memberships → applicableFee(); topics →
// recommendationRelevance() + the preferred route plan; mode/timing → the
// preferred plan. A preference nothing reads is worse than no preference: it
// teaches the user that answering does not matter. Do not add a question here
// without pointing at the code that consumes it.
//
// ‼️ IT ASKS NOTHING ABOUT HER POINTS. Progress is derived from records she
// adds one by one (onboarding.jsx:151, where PointsStep was deliberately
// removed); a self-reported figure would enter the NCHK export unevidenced.
// See cycleRecordCount in data.js for how the unknown case is presented.
//
// ── `welcome`: the step that runs straight after a purchase ────────────────
//
// ‼️ IT IS A STEP IN THIS SHEET, NOT A SHEET OF ITS OWN. The alternative —
// purchase sheet → congratulations sheet → setup sheet — is three modals in a
// row for someone who has just paid, which is how a celebration becomes an
// obstacle course. One mount, one dismiss.
//
// What it owes her, in this order:
//   1. confirmation that the charge did something (the hero)
//   2. WHAT is now on and WHERE each one lives, tappable, with a way there
//      (ProBenefitRows) — the actual question a new subscriber has
//   3. only then the optional questions, led by the membership one, which is
//      what turns the route's "from HK$X" into a real price
//
// ‼️ "LATER" ON THIS STEP CLOSES WITHOUT WRITING. The question steps save what
// she typed; this one has nothing to save, and stamping an empty coursePrefs
// here would record "asked and answered" for a question she never saw.
//
// ── `askProgress`: the last step, and the only one that is not a preference ─
//
// With nothing logged this cycle the route card cannot say anything at all, and
// the reason splits two ways: she has done courses and not logged them, or she
// has genuinely not started. This step asks — at the END, after the preferences,
// because it is the one question with a consequence and it deserves its own
// screen rather than a third button on a card.
//
// ‼️ ONLY THE "NOT STARTED" ANSWER IS STORED, AND IT STORES NO NUMBER. Zero adds
// no points, no Annex A rows and no baseline, so the NCHK export is untouched. A
// self-reported FIGURE would be an unevidenced baseline in a compliance
// document — which is why onboarding's PointsStep was deleted (onboarding.jsx:151)
// and why this step must never grow a "roughly how many points?" field.
// ─────────────────────────────────────────────────────────────────────────
function ProSetupSheet({ lang, profile, userId, welcome = false, askProgress = false,
                        onClose, onSaved, onNavigate, onLogRecord, onNotStarted }) {
  const isZh = lang === 'zh';
  const prior = (profile && profile.prefs && profile.prefs.coursePrefs) || {};
  // -1 is the welcome step; 0–2 are the three questions. Held as a number so
  // the existing "step + 1 / 3" progress line keeps working untouched for the
  // questions, and simply does not render on the welcome step.
  const [step, setStep] = React.useState(welcome ? -1 : 0);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');
  // The ARRAY, not a joined line — MembershipPicker owns the chips and the
  // free-text box and returns the merged, sanitised list. See the header above
  // membershipMatchesCourse (data.js) for why typing was never enough.
  const [memberships, setMemberships] = React.useState(
    () => (window.sanitiseMemberships
      ? window.sanitiseMemberships(profile?.memberships)
      : (Array.isArray(profile?.memberships) ? profile.memberships : [])),
  );
  const [topics, setTopics] = React.useState(
    () => (Array.isArray(prior.topics) ? prior.topics : []),
  );
  const [mode, setMode] = React.useState(prior.mode || null);        // 'online' | 'inPerson' | null
  const [timing, setTiming] = React.useState(
    () => (Array.isArray(prior.timing) ? prior.timing : []),          // 'weekend' | 'evening'
  );

  const TOPICS = (typeof window !== 'undefined' && window.TOPIC_LABEL) || {};
  const toggle = (list, setList, value) =>
    setList(list.includes(value) ? list.filter(v => v !== value) : [...list, value]);

  // The progress question is the last STEP, so it also decides what the timing
  // step's button says: "next" when there is something after it, "done" when
  // there is not.
  const PROGRESS_STEP = 3;
  const totalSteps = askProgress ? 4 : 3;

  // `advance` = save the preferences and move on instead of closing. Used by
  // the timing step when the progress question follows it.
  const save = async (skipRest, advance) => {
    if (busy || !userId || !window.DB?.upsertProfile) { onClose?.(); return; }
    setBusy(true); setErr('');
    // Sanitised again at the boundary, not only in the picker: this is the last
    // point before the write, and it is the one place a future caller could
    // hand us something unbounded.
    const list = window.sanitiseMemberships
      ? window.sanitiseMemberships(memberships)
      : (Array.isArray(memberships) ? memberships : []);
    // ONE upsert, not two: setPrefs() re-reads the profile before merging, so
    // calling it after upsertProfile would race its own write. Merge here and
    // send both fields together.
    const mergedPrefs = {
      ...(profile?.prefs || {}),
      coursePrefs: { topics, mode, timing, setAt: new Date().toISOString() },
    };
    const { data, error } = await window.DB.upsertProfile(userId, {
      memberships: list,
      prefs: mergedPrefs,
    });
    setBusy(false);
    if (error) {
      // Never trap her in the sheet on a failed write — this is optional data
      // and Pro already works without it.
      if (skipRest) { onClose?.(); return; }
      setErr(isZh ? '儲存失敗,可以稍後在個人設定再填。' : 'Could not save — you can add these later in your profile.');
      return;
    }
    onSaved?.(data);
    // The preferences are written either way; `advance` only decides whether
    // she is done or has one question left.
    if (advance) { setStep(PROGRESS_STEP); return; }
    onClose?.();
  };

  const Skip = () => (
    <button type="button" className="btn btn-ghost"
      style={{ padding: '6px 14px', fontSize: 12.5 }}
      disabled={busy} onClick={() => save(true)}>
      {isZh ? '稍後再說' : 'Later'}
    </button>
  );
  const Chip = ({ on, onClick, children }) => (
    <button type="button" className={'onb-chip' + (on ? ' on' : '')}
      aria-pressed={on} onClick={onClick}>{children}</button>
  );

  // The welcome step owns the sheet's title and hero; the question steps keep
  // the setup title they already had.
  const onWelcome = step === -1;

  return (
    <Sheet open={true} onClose={onClose}
      title={onWelcome ? 'Vital Pro' : (isZh ? '個人化 Vital Pro' : 'Set up Vital Pro')}>
      {onWelcome && (
        <div className="pro-hero is-member">
          <span className="pro-hero-mark">
            <VitalLogo size={15} color="var(--primary-ink)" />
            {isZh ? 'Vital Pro 會員' : 'Vital Pro member'}
          </span>
          {/* States what happened, and nothing about what she will achieve —
              the receipt is Apple's, the confirmation is ours. */}
          <h2>{isZh ? '訂閱完成，Vital Pro 已啟用。' : 'Subscription complete — Vital Pro is active.'}</h2>
          <p>
            {isZh
              ? '點一下任何一項,睇下點用同喺邊度用。'
              : 'Tap any of them to see how it works and where to find it.'}
          </p>
        </div>
      )}
      <div style={{ padding: '16px 20px 8px' }}>
        {!onWelcome && (
          <div className="muted-2" style={{ fontSize: 11.5, marginBottom: 10 }}>
            {/* "all optional" is true of the three preference steps and NOT of
                the progress question, whose two answers are both actions — so
                the promise is not repeated there. Closing the sheet still works
                from the ✕, which is why it needs no third "skip" button. */}
            {isZh
              ? `第 ${step + 1} / ${totalSteps} 步${step === PROGRESS_STEP ? '' : ' · 全部可以跳過'}`
              : `Step ${step + 1} of ${totalSteps}${step === PROGRESS_STEP ? '' : ' · all optional'}`}
          </div>
        )}

        {onWelcome && (
          <React.Fragment>
            <ProBenefitRows lang={lang} onNavigate={onNavigate} />
            {/* The one question worth asking at this moment, framed by what it
                changes rather than by what it is. Everything else waits. */}
            <div style={{
              fontSize: 12, lineHeight: 1.55, marginTop: 14, padding: '10px 12px',
              background: 'var(--primary-soft-2)', borderRadius: 12,
              border: '0.5px solid var(--border)',
            }}>
              {isZh
                ? '仲有一步：填咗你的專業會籍,補分路線就會用你實際付的價錢計,而唔係顯示「由…起」。'
                : 'One more thing: with your professional memberships on file, your route is costed at what you actually pay instead of “from HK$X”.'}
            </div>
          </React.Fragment>
        )}

        {step === 0 && (
          <React.Fragment>
            <h3 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 6px' }}>
              {isZh ? '你有冇專業會籍？' : 'Any professional memberships?'}
            </h3>
            <p className="muted" style={{ fontSize: 12.5, lineHeight: 1.55, marginTop: 0 }}>
              {isZh
                ? '揀返你有嘅機構。補分路線會用你實際付嘅價錢計,而唔係顯示「由…起」。'
                : 'Tick the bodies you belong to and your route is costed at what you actually pay, instead of showing "from".'}
            </p>
            {/* Was a free-text line whose two placeholder examples both failed
                to match anything (see data.js membershipMatchesCourse). */}
            <MembershipPicker lang={lang} value={memberships} onChange={setMemberships} />
          </React.Fragment>
        )}

        {step === 1 && (
          <React.Fragment>
            <h3 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 6px' }}>
              {isZh ? '你想睇邊方面嘅課程？' : 'Which areas interest you?'}
            </h3>
            <p className="muted" style={{ fontSize: 12.5, lineHeight: 1.55, marginTop: 0 }}>
              {isZh ? '揀幾多個都得。Vital 會把你揀嘅排先,唔會隱藏其他。'
                    : 'Pick any number. Vital ranks these first — it never hides the rest.'}
            </p>
            <div className="onb-roles" style={{ flexWrap: 'wrap', gap: 8 }}>
              {Object.keys(TOPICS).map(key => (
                <Chip key={key} on={topics.includes(key)}
                  onClick={() => toggle(topics, setTopics, key)}>
                  {isZh ? TOPICS[key].zh : TOPICS[key].en}
                </Chip>
              ))}
            </div>
          </React.Fragment>
        )}

        {step === 2 && (
          <React.Fragment>
            <h3 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 6px' }}>
              {isZh ? '幾時同點樣上堂方便啲？' : 'When and how does class suit you?'}
            </h3>
            {/* ‼️ Deliberately worded as a preference, not a filter. Measured
                2026-08-09: "online AND weekend" leaves 5 courses / 22.5 points
                in the actionable pool — not even half a 45-point requirement.
                So these reorder plans; they never remove options. */}
            <p className="muted" style={{ fontSize: 12.5, lineHeight: 1.55, marginTop: 0 }}>
              {isZh ? '呢啲係偏好,唔係篩選 —— Vital 會多提供一條符合你偏好的路線,但完整選擇一樣睇得到。'
                    : 'These are preferences, not filters — Vital adds a route that matches them, and always keeps the full one too.'}
            </p>
            <div className="onb-roles" style={{ flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
              <Chip on={mode === 'online'} onClick={() => setMode(mode === 'online' ? null : 'online')}>
                {isZh ? '網上為主' : 'Prefer online'}
              </Chip>
              <Chip on={mode === 'inPerson'} onClick={() => setMode(mode === 'inPerson' ? null : 'inPerson')}>
                {isZh ? '實體為主' : 'Prefer in person'}
              </Chip>
            </div>
            <div className="onb-roles" style={{ flexWrap: 'wrap', gap: 8 }}>
              <Chip on={timing.includes('weekend')} onClick={() => toggle(timing, setTiming, 'weekend')}>
                {isZh ? '週末' : 'Weekends'}
              </Chip>
              <Chip on={timing.includes('evening')} onClick={() => toggle(timing, setTiming, 'evening')}>
                {isZh ? '夜間' : 'Evenings'}
              </Chip>
            </div>
          </React.Fragment>
        )}

        {/* ── The progress question ─────────────────────────────────────────
            Last, and on its own screen, because it is the only step here with a
            consequence: it is what lets the route card say anything at all.
            Its two answers ARE the buttons — there is no "next" from here. */}
        {step === PROGRESS_STEP && (
          <React.Fragment>
            <h3 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 6px' }}>
              {isZh ? '你今個週期做過課程未？' : 'Have you done any courses this cycle?'}
            </h3>
            <p className="muted" style={{ fontSize: 12.5, lineHeight: 1.55, marginTop: 0 }}>
              {isZh
                ? '你今個週期仲未有紀錄。話咗我哋知,Vital 就可以計出你仲欠幾多、要幾多錢先補得完 —— 兩個答案都得。'
                : 'Nothing is logged for this cycle yet. Either answer lets Vital work out what is left and what it will cost.'}
            </p>
            {/* ‼️ NO NUMBER FIELD HERE, EVER. See the header: a self-reported
                points figure would enter the NCHK export unevidenced. "None" is
                the only quantity this step may record. */}
            <div className="muted-2" style={{ fontSize: 11.5, lineHeight: 1.5, marginTop: 8 }}>
              {isZh
                ? '你的學分只會由你逐個記錄的課程計出,唔會用估算。'
                : 'Your points are only ever counted from the courses you log — never from an estimate.'}
            </div>
          </React.Fragment>
        )}

        {err && (
          <div className="muted-2" style={{ fontSize: 11.5, marginTop: 10, color: 'var(--danger, inherit)' }}>
            {err}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 16, alignItems: 'center', flexWrap: 'wrap' }}>
          {step === PROGRESS_STEP ? (
            <React.Fragment>
              <button className="btn btn-primary" style={{ flex: 1, minWidth: 150 }}
                disabled={busy}
                onClick={() => { onClose?.(); onLogRecord?.(); }}>
                {isZh ? '記錄一個課程' : 'Log a course'}
              </button>
              <button type="button" className="btn btn-ghost"
                style={{ padding: '6px 14px', fontSize: 12.5 }}
                disabled={busy}
                onClick={async () => {
                  // Await the write before closing: the card behind this sheet
                  // reads the stored answer, and closing first would show her
                  // the unanswered card again for a beat.
                  setBusy(true);
                  try { await onNotStarted?.(); } finally { setBusy(false); }
                  onClose?.();
                }}>
                {isZh ? '我未開始' : 'Not started yet'}
              </button>
            </React.Fragment>
          ) : onWelcome ? (
            <React.Fragment>
              <button className="btn btn-primary" style={{ flex: 1 }}
                onClick={() => setStep(0)}>
                {isZh ? '開始使用（約 1 分鐘）' : 'Set up (about a minute)'}
              </button>
              {/* NOT <Skip />: that one saves. There is nothing to save here,
                  and writing an empty coursePrefs would mark the questions as
                  answered without her having seen them. */}
              <button type="button" className="btn btn-ghost"
                style={{ padding: '6px 14px', fontSize: 12.5 }}
                onClick={onClose}>
                {isZh ? '稍後再說' : 'Later'}
              </button>
            </React.Fragment>
          ) : step < 2 ? (
            <React.Fragment>
              <button className="btn btn-primary" style={{ flex: 1 }}
                disabled={busy} onClick={() => setStep(step + 1)}>
                {isZh ? '下一步' : 'Next'}
              </button>
              <Skip />
            </React.Fragment>
          ) : (
            <React.Fragment>
              {/* The timing step. It SAVES either way; `askProgress` only
                  decides whether saving is also the end. */}
              <button className="btn btn-primary" style={{ flex: 1 }}
                disabled={busy} onClick={() => save(false, askProgress)}>
                {busy
                  ? (isZh ? '儲存中…' : 'Saving…')
                  : askProgress ? (isZh ? '下一步' : 'Next') : (isZh ? '完成' : 'Done')}
              </button>
              <Skip />
            </React.Fragment>
          )}
        </div>
      </div>
    </Sheet>
  );
}

function ProStatusSheet({ lang, ent, readiness, onClose, onPersonalise, onNavigate, onSubscribe }) {
  const isZh = lang === 'zh';
  const [busy, setBusy] = React.useState(false);
  const [note, setNote] = React.useState('');

  // ‼️ A CODE IS NOT A SUBSCRIPTION, AND THIS SHEET IS MOSTLY ABOUT BILLING.
  // Everything below — the plan name, "renews automatically unless cancelled",
  // "manage in the App Store" — is true of an Apple subscription and false of a
  // Vital access code: there is no plan, nothing renews, and Apple's screen
  // would show her nothing. Telling her otherwise sets up the worst possible
  // surprise: access simply stops on a day she was told it would renew.
  const onPromo = ent?.source === 'promo';
  const planLabel = onPromo
    ? (isZh ? '優惠碼開通' : 'Access code')
    : proPlanLabel(ent?.productId, isZh);
  const untilIso = proLocalDateIso(onPromo ? ent?.promoUntil : ent?.expiresAt);
  const until = untilIso ? fmtDate(untilIso, lang) : '';
  // 'grace' = Apple is in billing retry. We deliberately keep Pro switched on
  // (see the webhook's STATUS_BY_TYPE note), so the ONLY thing standing between
  // her and a silent downgrade is this banner.
  const inGrace = ent?.status === 'grace';

  const restore = async () => {
    if (busy || !window.PurchaseAPI?.isSupported?.()) return;
    setBusy(true); setNote('');
    await window.PurchaseAPI.restore();
    const res = await window.DB.syncEntitlement();
    setBusy(false);
    setNote(res?.pro
      ? (isZh ? '已同步最新的訂閱狀態。' : 'Subscription status is up to date.')
      : (isZh ? '找不到可回復的訂閱。' : 'No previous subscription found to restore.'));
  };

  return (
    <Sheet open={true} onClose={onClose} title="Vital Pro">
      <div className="pro-hero is-member">
        <span className="pro-hero-mark">
          <VitalLogo size={15} color="var(--primary-ink)" />
          {isZh ? 'Vital Pro 會員' : 'Vital Pro member'}
        </span>
        <h2>{isZh ? 'Vital Pro 已啟用。' : 'Vital Pro is active.'}</h2>
        <p>
          {until
            ? (isZh ? `${planLabel} · 本期有效至 ${until}` : `${planLabel} · current period runs through ${until}`)
            : (isZh ? `${planLabel} · 訂閱中` : `${planLabel} · active`)}
        </p>
      </div>

      <div style={{ padding: '16px 20px 8px' }}>
        {/* Billing retry. role="alert" so it is announced, not merely coloured —
            this is the one message on the sheet with a deadline attached. */}
        {inGrace && (
          <div role="alert" style={{
            fontSize: 12.5, lineHeight: 1.55, marginBottom: 14, padding: '10px 12px',
            borderRadius: 12,
            color: 'var(--danger)',
            background: 'color-mix(in oklch, var(--danger) 8%, transparent)',
            border: '0.5px solid color-mix(in oklch, var(--danger) 30%, transparent)',
          }}>
            {isZh
              ? '付款出現問題,Apple 正在重試。你的 Pro 功能暫時保持開啟 —— 請在 App Store 更新付款方式,否則本期結束後會失效。'
              : 'There is a problem with your payment and Apple is retrying it. Pro stays on for now — update your payment method in the App Store, or it will lapse at the end of this period.'}
          </div>
        )}

        {/* Tap a row for what it does and where it lives; the button inside
            goes there (ProBenefitRows). A subscriber who cannot FIND what she
            bought is indistinguishable from one who was not charged. */}
        <div className="muted-2" style={{ fontSize: 11.5, marginBottom: 4 }}>
          {readiness?.loading
            ? (isZh ? '正在檢查你的設定…' : 'Checking your setup…')
            : (isZh
                ? `所有 Pro 權益已解鎖 · ${readiness?.readyCount || 0} 項已按你的設定準備好`
                : `Every Pro benefit is unlocked · ${readiness?.readyCount || 0} ready with your settings`)}
        </div>
        <ProBenefitRows lang={lang} onNavigate={onNavigate} readiness={readiness} />

        {/* The lapse promise again, where a subscriber weighing cancellation is
            the one reading it. Saying it HERE — next to the cancel path rather
            than only on the sales screen — is the difference between a promise
            and a marketing line. */}
        <div className="muted-2" style={{ fontSize: 11.5, lineHeight: 1.55, marginTop: 10 }}>
          {isZh
            ? '取消後你仍可完整讀取、匯出及刪除自己的所有紀錄與證書 —— 你的合規證明永遠屬於你。'
            : 'If you cancel you keep full access to read, export and delete every record and certificate you already have — your compliance evidence is always yours.'}
        </div>

        {/* The setup questions are skippable at purchase, so most subscribers
            will not have answered them — and the membership one is what decides
            whether her route can name a price or only "from HK$X". This is the
            way back to it. Rendered only when the host wired a handler, so the
            sheet still works anywhere it is shown without one. */}
        {onPersonalise && (
          <button className="btn btn-ghost" style={{ width: '100%', marginTop: 14 }}
            onClick={onPersonalise}>
            {isZh ? '個人化設定（會籍、興趣、時間）' : 'Personalise (memberships, interests, timing)'}
          </button>
        )}

        {/* ‼️ THE ONE CONTROL THAT MUST NOT APPEAR FOR A CODE HOLDER. Apple's
            subscription screen has nothing of hers on it; sending her there to
            "manage" something that does not exist is how a support message
            starts. She gets the honest ending instead — what happens on the
            expiry date, and the way to keep Pro if she wants it. */}
        {onPromo ? (
          <React.Fragment>
            <div style={{
              fontSize: 12.5, lineHeight: 1.55, marginTop: 12, padding: '10px 12px',
              background: 'var(--primary-soft-2)', borderRadius: 12,
              border: '0.5px solid var(--border)',
            }}>
              {isZh
                ? `你嘅 Pro 由優惠碼開通,${until ? `到 ${until} ` : ''}到期後會自動回到免費版 —— 唔會扣錢,亦唔會自動續期。你的紀錄、匯出同法規資訊永遠免費。`
                : `Your Pro came from an access code. ${until ? `On ${until} it` : 'When it ends it'} simply returns to Free — nothing is charged and nothing renews. Your records, exports and the regulations stay free either way.`}
            </div>
            {onSubscribe && window.PurchaseAPI?.isSupported?.() && (
              <button className="btn btn-primary" style={{ width: '100%', marginTop: 10 }}
                onClick={onSubscribe}>
                {isZh ? '睇下訂閱方案' : 'See subscription plans'}
              </button>
            )}
          </React.Fragment>
        ) : (
          <button className="btn btn-primary" style={{ width: '100%', marginTop: 10 }}
            onClick={() => openProLink(PRO_LINKS.manage)}>
            {isZh ? '在 App Store 管理訂閱' : 'Manage subscription in the App Store'}
          </button>
        )}
        {!onPromo && (
        <div className="muted-2" style={{ fontSize: 11, textAlign: 'center', marginTop: 8, lineHeight: 1.5 }}>
          {/* We know the period END. We do NOT store an auto-renew flag, so we
              must not print "renews on" as if it were a fact — she may have
              already cancelled. This wording is true either way. */}
          {until
            ? (isZh
                ? `除非在 ${until} 前至少 24 小時取消,否則會自動續期。取消、退款與更改方案全部在 App Store 帳戶設定處理。`
                : `Renews automatically unless cancelled at least 24 hours before ${until}. Cancellations, refunds and plan changes are all handled in your App Store account settings.`)
            : (isZh
                ? '取消、退款與更改方案全部在 App Store 帳戶設定處理。'
                : 'Cancellations, refunds and plan changes are handled in your App Store account settings.')}
        </div>
        )}

        {note && (
          <div className="muted-2" role="status" style={{ fontSize: 11.5, textAlign: 'center', marginTop: 8 }}>
            {note}
          </div>
        )}

        {/* Guideline 3.1.1 — Restore must stay reachable for someone who has
            reinstalled or switched devices, and this sheet is now what the
            Profile entry opens for a subscriber.
            Support sits with them because THIS is the sheet a nurse opens when
            something is wrong with a charge — and until now the app offered her
            nowhere to write from. It is an ADDITION: Terms and Privacy are
            required by 3.1.2 and neither may be replaced by it. */}
        <div className="pro-footer-links" style={{ marginTop: 6 }}>
          <button type="button" disabled={busy} onClick={restore}>
            {isZh ? '回復購買' : 'Restore Purchases'}
          </button>
          <span className="pro-footer-sep" aria-hidden="true">·</span>
          <button type="button" onClick={() => openProLink(PRO_LINKS.support)}>
            {isZh ? '支援' : 'Support'}
          </button>
          <span className="pro-footer-sep" aria-hidden="true">·</span>
          <button type="button" onClick={() => openProLink(PRO_LINKS.terms)}>
            {isZh ? '使用條款' : 'Terms'}
          </button>
          <span className="pro-footer-sep" aria-hidden="true">·</span>
          <button type="button" onClick={() => openProLink(PRO_LINKS.privacy)}>
            {isZh ? '私隱政策' : 'Privacy'}
          </button>
        </div>
      </div>
    </Sheet>
  );
}

// The ONE component every Pro entry point should render. It reads the server
// entitlement and shows the purchase sheet or the membership sheet accordingly,
// so no call site can accidentally show a subscriber a price again — which is
// exactly how the original bug happened (three separate call sites, each
// hardcoded to PaywallSheet).
//
// `initialEnt` is the caller's already-loaded entitlement, used only to pick
// the right sheet on the FIRST frame; the hook's own read then takes over.
// Without it a subscriber would see the paywall flash before it swapped.
function ProSheet({ lang, records, profile, userId, initialEnt, trigger, onClose, onPurchased, onEarnInstead, onPersonalise, onNavigate }) {
  const ent = useProEntitlement(userId);
  // Only a COMPLETED entitlement snapshot may choose the first frame. Some
  // callers mount while their own entitlement hook is still loading; treating
  // that placeholder (`pro: false`) as authoritative briefly showed the
  // paywall before the server response replaced it with the member sheet.
  // Besides looking like two stacked pages, that is an especially confusing
  // thing to show someone who already paid. Unknown therefore stays neutral.
  const hasInitialEnt = !!initialEnt && initialEnt.loading === false;
  const shown = (ent.loading && hasInitialEnt) ? { ...initialEnt, loading: true } : ent;
  const readiness = useProBenefitReadiness(userId, profile, records, !!shown.pro);
  // ‼️ A CODE HOLDER MUST STILL BE ABLE TO REACH THE PURCHASE SCREEN. This sheet
  // routes on `pro`, and a code makes `pro` true — so without an explicit way
  // through, the one person most likely to convert (she is using Pro right now
  // and can see it ending) would have no way to buy it.
  const [forcePaywall, setForcePaywall] = React.useState(false);
  if (ent.loading && !hasInitialEnt) {
    return (
      <Sheet open onClose={onClose} title="Vital Pro">
        <div className="pro-sheet-loading" role="status" aria-live="polite">
          <span className="vital-spinner" aria-hidden="true" />
          <span>{lang === 'zh' ? '正在確認會員資格…' : 'Checking membership…'}</span>
        </div>
      </Sheet>
    );
  }
  if (shown.pro && !forcePaywall) {
    return <ProStatusSheet lang={lang} ent={shown} onClose={onClose}
      readiness={readiness}
      onPersonalise={onPersonalise} onNavigate={onNavigate}
      onSubscribe={() => setForcePaywall(true)} />;
  }
  return (
    <PaywallSheet lang={lang} records={records} profile={profile} trigger={trigger}
      onClose={onClose} onPurchased={onPurchased} onEarnInstead={onEarnInstead} />
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  ProEntryCard — the Profile entry point.
//
//  It is a CARD and not a list Row for two reasons, one commercial and one
//  functional. As a row it read as the sixth item in a settings list, which is
//  where a subscription offer goes to be ignored. And a subscriber's status —
//  the thing she paid for — deserves more than 12px of grey subtitle.
//
//  ‼️ REQUIRED BY APP REVIEW. This is the only findable path to the purchase
//  sheet (Guideline 2.1: "unable to locate the in-app purchases") and to
//  Restore Purchases (3.1.1). Restyling it is fine; removing it is a rejection.
//  Its visibility rule is unchanged: shown when something can be bought OR
//  something has been.
// ─────────────────────────────────────────────────────────────────────────
function ProEntryCard({ lang, ent, onOpen }) {
  const isZh = lang === 'zh';
  const member = !!ent?.pro;
  // A code's end date lives in its own column — reading expiresAt would show a
  // code holder either nothing or, worse, a stale Apple date.
  const untilIso = proLocalDateIso(ent?.source === 'promo' ? ent?.promoUntil : ent?.expiresAt);
  const until = untilIso ? fmtDate(untilIso, lang) : '';
  const inGrace = ent?.status === 'grace';

  if (member) {
    return (
      <button type="button" className="pro-entry is-member" onClick={onOpen}>
        <span className="pro-entry-top">
          <span className="pro-entry-mark">
            <Icon name="check-circ" size={13} stroke={2.2} />
            {isZh ? 'Vital Pro 會員' : 'Vital Pro member'}
          </span>
          <span className="pro-entry-chev" aria-hidden="true">
            <Icon name="chev-r" size={16} stroke={1.8} />
          </span>
        </span>
        <span className="pro-entry-title">
          {isZh
            ? `${ent?.source === 'promo' ? '優惠碼開通' : proPlanLabel(ent?.productId, true)}${until ? ` · 有效至 ${until}` : ''}`
            : `${ent?.source === 'promo' ? 'Access code' : proPlanLabel(ent?.productId, false)}${until ? ` · through ${until}` : ''}`}
        </span>
        <span className={'pro-entry-sub' + (inGrace ? ' is-warn' : '')}>
          {inGrace
            ? (isZh ? '付款出現問題 —— 請更新付款方式' : 'Payment problem — update your payment method')
            : (isZh ? '新課程通知 · 補分計算 · 證書自動整理' : 'Course alerts · gap planning · AI filing')}
        </span>
      </button>
    );
  }

  return (
    <button type="button" className="pro-entry" onClick={onOpen}>
      <span className="pro-entry-top">
        <span className="pro-entry-mark">
          <VitalLogo size={13} color="var(--primary-ink)" />
          Vital Pro
        </span>
        <span className="pro-entry-chev" aria-hidden="true">
          <Icon name="chev-r" size={16} stroke={1.8} />
        </span>
      </span>
      <span className="pro-entry-title">
        {isZh ? '有啱你嘅新課程就通知你' : 'Get told when a course that fits appears'}
      </span>
      <span className="pro-entry-sub">
        {isZh
          ? '有啱你嘅新課程就通知你,幫你計好點樣補返啲分,證書自動整理好。你的紀錄同匯出永遠免費。'
          : 'Your CNE route, AI certificate filing and one-tap audit packs. Your records and exports stay free.'}
      </span>
    </button>
  );
}

// The "she is a member" mark on the Profile identity card. Small on purpose:
// status should be legible at a glance, not shout at her every time she opens
// her own profile.
function ProBadge({ lang }) {
  return (
    <span className="pro-pill" aria-label={lang === 'zh' ? 'Vital Pro 會員' : 'Vital Pro member'}>
      <Icon name="sparkle" size={9} stroke={2.2} color="currentColor" />
      PRO
    </span>
  );
}

;Object.assign(typeof window !== 'undefined' ? window : globalThis, { PRO_BENEFITS, FREE_INCLUDED, PRO_LINKS, openProLink, PRO_ENTITLEMENT_EMPTY, useProEntitlement, PRO_READINESS_EMPTY, deriveProBenefitReadiness, useProBenefitReadiness, proPlanLabel, proLocalDateIso, proPackageKind, proPriceNumber, proFormatAmount, proIntroOffer, proAnnualSaving, proReadinessLabel, ProBenefitRows, ProActivationSuccessScreen, PaywallSheet, ProSetupSheet, ProStatusSheet, ProSheet, ProEntryCard, ProBadge });
